import { ApiError, useSdk, isOidcBasedProvider } from "@fondationcss/sdk";
import type { AvailabilityFrameRequest, IFetchWrapper, UnavailabilityRequest, UnavailableRange, UserAvailability } from "@fondationcss/sdk";
import type { ExamRequest, ExamResponse, ExamsResponse, ExamServiceTypeSuggestionRequest, ExamServiceTypeSuggestionsResponse, ExamUpdateRequest, GroupExamRequest } from "./dto";
import type { ArchiveImpactResponse } from "./dto/archive-impact";
import type { TeacherTransferAssignment, TeacherTransferInventoryResponse } from "./dto/teacher-transfer";
import type { HoursTransfer, HoursTransferInventoryResponse, HoursTransferRequest } from "./dto/hours-transfer";
import type { ConsolidatedViewResponse } from "./dto/consolidated-view-response";
import type { ExamAssignmentReportRequest } from "./dto/exam-assignment-report-request";
import type { ImportResultDto, JadeFrequentationsImportResultDto, JadeResultsImportResultDto, JadeRosterImportResultDto, IndividualRosterImportDefaults } from "./dto/export-import-dto";
import type { FrequentationResponse, FrequentationsResponse, PagedFrequentationsResponse } from "./dto/frequentation-response";
import type { FrequentationModuleResultsResponse } from "./dto/frequentation-module-results-response";
import type { FrequentationModuleHoursResponse } from "./dto/frequentation-module-hours-response";
import type { FrequentationModuleAvailabilityOutlook, FrequentationModuleAvailabilityResponse } from "./dto/frequentation-module-availability-response";
import type { LearningModuleRequest } from "./dto/learning-module-request";
import type { LearningModuleResponse } from "./dto/learning-module-response";
import type { LearningModulesResponse } from "./dto/learning-modules-response";
import type { LearningProgramRequest } from "./dto/learning-program-request";
import type { LearningProgramResponse } from "./dto/learning-program-response";
import type { LearningProgramsResponse } from "./dto/learning-programs-response";
import type { SequenceBlock } from "./model/sequence-block";
import type { ModulePlanResponse, ModulePlansBatchResponse } from "./dto/module-plan-response";
import type { ScheduleAxisRequest, ScheduleAxisResponse } from "./dto/schedule-axis";
import type { TeacherDaySheetResponse } from "./dto/teacher-day-sheet-response";
import type { BulkPresenceResponse } from "./dto/bulk-presence-response";
import type { ExamSupervisionDaySheet, ExamSupervisionEntry, ExamSupervisionPresencesRequest } from "./dto/exam-supervision";
import type {
    RegisterSpecialistRequest, SpecialistAgenda, SpecialistBookingRequest, SpecialistBookingRooms,
    SpecialistBookingRoomsQuery, SpecialistCandidatesResponse, SpecialistDaySheetResponse, SpecialistDirectoryResponse,
    SpecialistGroupAttendance, SpecialistGroupProgression, SpecialistGroupsResponse,
    SpecialistPresencesRequest, SpecialistRosterResponse, SpecialistRosterRow, SpecialistSchedule, SpecialistsResponse,
    SpecialistStudent, SpecialistStudentsResponse,
} from "./dto/specialist";
import type { TeacherDailyReportResponse } from "./dto/teacher-daily-report-response";
import type { PagedGridQuery, PagedGridResult } from "@fondationcss/ui";
import type { PresenceRequest } from "./dto/presence-request";
import type { PresenceResponse } from "./dto/presence-response";
import type { PresencesByStudentResponse } from "./dto/presences-by-student-response";
import type { StudentJourneyResponse } from "./dto/student-journey-response";
import type { PagedStudentsResponse } from "./dto/student-response";
import type { AvailableTeachersResponse, PagedTeachersResponse, TeacherResponse, TeachersResponse } from "./dto/teacher-response";
import type { TeacherAvailabilitiesResponse, TeacherAvailabilityRequest, TeacherAvailabilityResponse } from "./dto/teacher-availability";
import type { AteEnterprise, AteEnterpriseContact } from "./model/ate-enterprise";
import type { AteHAC, AteHACEntry } from "./model/ate-hac";
import type { AteActivityAssessment, AteWorkPlacementActivity, AteWorkPlacementActivityNote } from "./model/ate-journal";
import type { AteQuestionnaire } from "./model/ate-questionnaire";
import type { AteActivityTemplate, AteAssessmentRatingGroup, AteHACItem, AteHACTemplate, AteQuestionnaireItemTemplate, AteQuestionnaireTemplate } from "./model/ate-reference-data";
import type { AteWorkPlacementVisit, AteWorkPlacementVisitNote } from "./model/ate-visit";
import type { AteWorkPlacement, AteWorkPlacementDashboardItem, AteWorkPlacementDashboardSummary, AteWorkPlacementNote } from "./model/ate-work-placement";
import type { AteStudentJournalEntry, AteStudentJournalRequest, AteStudentStageDetail, AteStudentStageSummary } from "./model/ate-student-portal";
import type { ExamListItem } from "./model/exam-list-item";
import type { ExamNote, ExamNoteCountsResponse, ExamNotesResponse } from "./model/exam-note";
import type { StudentNote, StudentSharedNote } from './model/student-note';
import type { CreateFrequentationNoteTypeDefinitionRequest, FrequentationNoteTypeDefinition, FrequentationNoteTypeDefinitionsResponse, UpdateFrequentationNoteTypeDefinitionRequest } from './model/frequentation-note-type-definition';
import type { CreateFrequentationTagRequest, FrequentationTag, FrequentationTagAssignmentsResponse, FrequentationTagsResponse, FrequentationTagValueRequest, UpdateFrequentationTagRequest } from './model/frequentation-tag';
import type { GricsImportKindOverview, GricsImportRun, StartGricsImportRunRequest } from './model/grics-import';
import type {
    ActiveGroupsResponse,
    CreateGroupRequest,
    DuplicateGroupRequest,
    GroupResponse,
    GroupsResponse,
    UpdateGroupRequest
} from "./model/group";
import type { GroupDashboardStats } from "./model/group-dashboard-stats";
import type {
    FrequentationSummary,
    FrequentationSummaryOptions,
    PresenceSummary,
    PresenceSummaryOptions,
    TeacherLoadSummary,
    TeacherLoadSummaryOptions
} from "./model/teaching-summary";
import type { GroupMemberResponse, GroupMembersByProgramResponse, GroupMembersResponse } from "./model/group-membership";
import type { GroupStatsResponse } from "./model/group-stats";
import type { CreateRoomRequest, Room, RoomOccupancy, RoomOccupancyQuery, RoomOwnerEnterprise, RoomScope, UpdateRoomRequest } from './model/room';
import type { CreateResourceRequest, Resource, SetResourceProgramsRequest, UpdateResourceRequest } from './model/resource';
import type {
    CreatePlanningRequest,
    PlanningResponse,
    PlanningsResponse,
    UpdatePlanningRequest
} from "./model/planning";
import type {
    ApplyPlanningSlotsRequest,
    ApplyPlanningSlotsResponse,
    AssignmentBusyRequest,
    AssignmentBusyResponse,
    AssignmentConflictsRequest,
    AssignmentConflictsResponse,
    ClearSlotAssignmentsRequest,
    PatchPlanningSlotRequest,
    PatchPlanningSlotsRequest,
    ReleaseTeacherSlotsRequest,
    PlanningSlot,
    PlanningSlotsResponse,
    UpdatePlanningSlotsRequest
} from "./model/planning-slot";
import type { EffectivePlanResponse } from "./dto/effective-plan-response";
import type { Presence } from "./model/presence";
import type {
    ScheduleChainInfo,
    ScheduleChainOperation,
    ScheduleChainOperationResult,
    ScheduleChainOwnerType
} from "./model/schedule-personalization";
import type { CreatePresenceTypeDefinitionRequest, PresenceTypeDefinition, PresenceTypeDefinitionsResponse, UpdatePresenceTypeDefinitionRequest } from './model/presence-type-definition';
import type { CreatePresenceStatusDefinitionRequest, PresenceStatusDefinition, PresenceStatusDefinitionsResponse, UpdatePresenceStatusDefinitionRequest } from './model/presence-status-definition';
import type {
    ProgramModuleSequencesResponse,
    UpdateProgramModuleSequencesRequest,
    UpdateProgramModuleSequencesBatchRequest
} from "./model/program-module-sequence";
import type {
    ProgramPhasesResponse,
    UpdateProgramPhasesRequest,
    PlanningPhasesResponse,
    UpdatePlanningPhasesRequest
} from "./model/program-phase";
import type {
    RemedyCheckRequest, RemedyCheckResponse, SolveGroupsRequest, SolvePrecheckRequest,
    SolvePrecheckResponse,
} from './model/scheduling';
import type { SchedulingV2Request, SchedulingV2Response, DatasetPresetV2 } from './model/scheduling-v2';
import { toRegenerationLosses, toSessionReplacements, toTakeCourseResult } from './model/group-course';
import type { GeneratedSession, GroupCourse, GroupCourseInput, GroupCourseDefaults, GroupCourseListQuery, GroupCoursePage, GroupCourseSessionResult, GroupCourseSummary, GroupCourseUpdateResult, SessionWriteOutcome, TakeCourseResult, UpdateGroupCourseOutcome } from './model/group-course';
import type { CreateSiteRequest, Site, UpdateSiteRequest } from './model/site';
import type { InitializationStatusResponse } from './model/initialization-status';
import type { Student, UpdateStudentCoordinatesRequest } from "./model/student";
import type { StudentAddress } from "./model/student-address";
import type {
    AddSubmissionFilesRequest,
    AddSubmissionFilesResponse,
    CreateAbsenceJustificationRequest,
    CreateExamSubmissionRequest,
    CreateSubmissionResponse,
    PresenceSubmissionsResponse,
    StudentSubmissionResponse,
    StudentSubmissionsResponse,
    SubmissionKind
} from "./model/student-submission";
import type { CreateVacancyRequest, VacanciesResponse } from './dto/vacancies';
import type { MeqDependencies, MeqProgramme, MeqProgrammeSummary, PagedMeqProgrammesResponse } from './dto/meq-reference';
import type { AbsenceDay, AbsenceRange } from './dto/absence-day';
import type { BlockExamCell } from './dto/block-exam-cell';

/** Per-call request options (e.g. a custom errorHandler) forwarded to the underlying fetch wrapper. */
export type SdkRequestOptions = NonNullable<Parameters<IFetchWrapper['post']>[2]>;

export interface DemonstrationConfigurationResponse {
    id: string;
    name: string;
    configurationJson: string;
}

export interface GeneratedPersonResponse {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ApplyConfigurationResult {
    success: boolean;
    errorMessage: string | null;
    studentsCreated: number;
    students: GeneratedPersonResponse[];
    teachersCreated: number;
    teachers: GeneratedPersonResponse[];
    programsActivated: number;
    activatedProgramNames: string[];
    groupsCreated: number;
    /** `unplanned`: left for the user to plan, as the product creates a group (#1462). */
    groups: { id: string; name: string; programName: string; startDate: string; programId: string; unplanned: boolean }[];
    skippedStarts: string[];
    presencesCreated: number;
    centerCreated: number;
    roomsCreated: number;
    calendarsCreated: number;
    /** Working frames given to the created teachers in the Fondation (#923). */
    workFramesCreated: number;
    /** Competence offerings declared for the created teachers (#923). */
    offeringsCreated: number;
    schedulingSolved: boolean | null;
    schedulingSolutionStats: Record<string, unknown> | null;
    /** The group-planning showcase (#1462): absent when the configuration starts no group. */
    groupShowcase?: DemoShowcase | null;
    /** The individualized-planning showcase (#1462). */
    individualShowcase?: DemoShowcase | null;
}

/**
 * A program and one of its teachers a demonstration is meant to be shown with (#1462), and what is
 * left there for the user to plan: groups (group mode) or enrolments (individualized mode).
 */
export interface DemoShowcase {
    programId: string;
    programTitle: string;
    siteId: string | null;
    teacher: GeneratedPersonResponse | null;
    unplannedIds: string[];
}

export interface CleanupResult {
    success: boolean;
    errorMessage: string | null;
    rowsDeleted: Record<string, number>;
}

export interface RemoveConfigurationResult {
    success: boolean;
    errorMessage: string | null;
    programsDisabled: string[];
    presencesDeleted: number;
    examsDeleted: number;
    frequentationsDeleted: number;
    groupsDeleted: number;
    planningsDeleted: number;
    teachersDeleted: number;
    teachersUnassigned: number;
    /** Competence offerings removed with the programs (#923). */
    offeringsDeleted: number;
    /** Working frames that went with the deleted teachers (#923). */
    workFramesDeleted: number;
    sitesDeleted: number;
    roomsDeleted: number;
    calendarsDeleted: number;
    skipped: string[];
}

export interface DataStatsResult {
    students: number;
    teachers: number;
    groups: number;
    programs: number;
    activePrograms: number;
    frequentations: number;
    presences: number;
    exams: number;
    sites: number;
    users: number;
    ateWorkPlacements: number;
}

const DEFAULT_SEARCH_MAX_RESULTS = 15;

function buildPagedParams(query: PagedGridQuery): URLSearchParams {
    const params = new URLSearchParams();
    params.set('page', String(query.page));
    params.set('pageSize', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDesc) params.set('sortDesc', 'true');
    if (query.groupBy) params.set('groupBy', query.groupBy);
    // Repeated rather than joined: the key may hold a comma — a program is free to be called
    // « Mécanique, option lourde ». ASP.NET binds repeated keys to a string[] (#721).
    for (const key of query.collapsedGroups ?? []) params.append('collapsedGroups', key);
    if (query.jumpToLetter) params.set('jumpToLetter', query.jumpToLetter);
    if (query.filters) {
        for (const [key, value] of Object.entries(query.filters)) {
            if (value != null) params.set(key, value);
        }
    }
    return params;
}

export interface ExamFilters {
    programId?: string;
    groupId?: string;
    moduleId?: string;
    frequentationId?: string;
    teacherId?: string;
    dateFrom?: string;
    dateTo?: string;
    result?: string;
    studentFilter?: string | null;  // JSON-encoded StudentFilterCriteria
}

/** Options of the date-range presence reads (`getPresencesByDateRange`, `getTeacherPresencesByDateRange`). */
export interface PresenceDateRangeOptions {
    /**
     * #1226: a teacher's reading also carries the periods their students spend « avec un
     * spécialiste » — SpecialistSupport rows credited to the specialist, which the teacher filter
     * otherwise leaves out. Groupe › Présences and Assignations ask for it to say where the student
     * is; the staff reading of every row already holds them.
     */
    withSpecialistBookings?: boolean;
}

export const getCustomSdk = () => {

    const sdk = useSdk();

    // LearningProgram API functions
    const learningPrograms = async () => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/`);
    const availableLearningPrograms = async (options?: SdkRequestOptions) => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/available`, options);
    const getLearningProgram = async (id: string) => sdk.fetchWrapper.get<LearningProgramResponse>(`/learningPrograms/${id}`);
    const addLearningProgram = async (data: LearningProgramRequest) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/`, data);
    const updateLearningProgram = async (id: string, data: LearningProgramRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}`, data, options);
    const setLearningProgramAvailability = async (id: string, isAvailable: boolean, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}/setAvailability`, { isAvailable }, options);
    const cloneLearningProgram = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/${id}/clone`, {}, options);

    // plannedArchived: the archived competences a group still follows come too (a teacher record ticks them).
    const learningModules = async (options?: SdkRequestOptions, query?: { plannedArchived?: boolean }) =>
        sdk.fetchWrapper.get<LearningModulesResponse>(query?.plannedArchived ? `/learningModules/?plannedArchived=true` : `/learningModules/`, options);
    const learningModulesByProgram = async (programId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningPrograms/${programId}/learningModules/`);
    const getAvailableLearningModules = async (
        programId: string,
        options: { frequentationId?: string; date?: string; periodId?: string; teacherId?: string; forExam?: boolean } = {}
    ) => {
        const params = new URLSearchParams({ programId });
        // An exam is not a course seat: group-given competencies come back as ordinary ones.
        if (options.forExam) params.set('forExam', 'true');
        if (options.frequentationId) params.set('frequentationId', options.frequentationId);
        if (options.date) params.set('date', options.date);
        if (options.periodId) params.set('periodId', options.periodId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        return sdk.fetchWrapper.get<LearningModulesResponse>(`/learningModules/available?${params.toString()}`);
    };
    const getLearningModule = async (id: string, options?: SdkRequestOptions) => sdk.fetchWrapper.get<LearningModuleResponse>(`/learningModules/${id}`, options);
    const addLearningModule = async (data: LearningModuleRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<LearningModuleResponse>(`/learningModules/`, data, options);
    const updateLearningModule = async (id: string, data: LearningModuleRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}`, data, options);
    // #990: written apart from the competency PUT, which replaces the record and ignores both.
    const setLearningModuleResponsibleTeacher = async (id: string, teacherId: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningModules/${id}/responsibleTeacher`, { teacherId }, options);
    const setLearningModuleTags = async (id: string, tags: string[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningModules/${id}/tags`, { tags }, options);
    // #1237: the MEQ duration of a competency outside the catalogue, null to clear it; the server
    // refuses a catalogue value (409) and a duration that is not whole units of 15 h, or is above 990 h (400).
    const setLearningModuleNormativeHours = async (id: string, normativeNumberOfHours: number | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningModules/${id}/normativeHours`, { normativeNumberOfHours }, options);
    const archiveLearningModule = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}/archive`, {}, options);
    const restoreLearningModule = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}/restore`, {}, options);
    const archivedLearningModulesByProgram = async (programId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningPrograms/${programId}/learningModules/archived`);

    // Teacher API functions
    // `includePendingVacancies`: the solver postes no « Appliquer » confirmed yet — hidden from
    // every picker, asked for by the simulation that proposes them alone (#1184).
    const teachers = async (options: { includePendingVacancies?: boolean } = {}) => {
        const params = new URLSearchParams();
        if (options.includePendingVacancies) params.set('includePendingVacancies', 'true');
        const query = params.toString();
        return sdk.fetchWrapper.get<TeachersResponse>(query ? `/teachers/?${query}` : `/teachers/`);
    };
    const getTeacher = async (id: string, options?: SdkRequestOptions) => sdk.fetchWrapper.get<TeacherResponse>(`/teachers/${id}`, options);
    const teachersPaged = async (query: PagedGridQuery): Promise<PagedTeachersResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedTeachersResponse>(`/teachers/paged?${params.toString()}`);
    };
    // The MEQ reference, read-only: the backend reads Agora, the browser never holds its token.
    const meqProgrammesPaged = async (query: PagedGridQuery): Promise<PagedMeqProgrammesResponse> => {
        const params = buildPagedParams(query);
        const page = await sdk.fetchWrapper.get<PagedGridResult<MeqProgrammeSummary>>(`/MeqReference/Programmes/Paged?${params.toString()}`); // i18n-ignore: wire value, route path or technical text; not user-facing wording
        return { ...page, items: page.items.map((item) => ({ ...item, id: item.code })) };
    };
    const getMeqProgramme = async (code: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<MeqProgramme>(`/MeqReference/Programmes/${encodeURIComponent(code)}`, options);
    const getMeqDependencies = async (code: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<MeqDependencies>(`/MeqReference/Programmes/${encodeURIComponent(code)}/Dependencies`, options);
    /** The printable dependencies of a programme (PDF): the transitions left once the graph is simplified. */
    const generateMeqDependenciesReport = async (code: string, categories: string[], language: 'fr' | 'en', options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/MeqReference/Programmes/${encodeURIComponent(code)}/Report`, { categories, language }, options); // i18n-ignore: wire value, route path or technical text; not user-facing wording
    const getCurrentTeacher = async () => sdk.fetchWrapper.get<{ teacher: TeacherResponse['teacher'] | null }>(`/teachers/current`);
    const getTeacherPlanning = async (teacherId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?teacherId=${teacherId}`);
    };
    const getGroupPlanning = async (groupId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?fallbackGroupId=${groupId}`);
    };
    const getTeachersCurrentActivity = async () =>
        sdk.fetchWrapper.get<import('./dto/teachers-activity-response').TeachersActivityResponse>(`/teachers/currentActivity`);
    const isTeacherGroupMappedToEntraId = async () => sdk.fetchWrapper.get<{ isMappedToEntraId: boolean }>(`/teachers/isTeacherGroupMappedToEntraId`);
    // #722 — ses cours à venir ET les compétences dont il est enseignant désigné.
    const getTeacherArchiveImpact = async (id: string) =>
        sdk.fetchWrapper.get<ArchiveImpactResponse>(`/teachers/${id}/archiveImpact`);
    // #722, the transfer half: the departing teacher's future commitments as per-block choices,
    // then archive+transfer as one transaction (every block must name its replacement).
    const getTeacherTransferInventory = async (id: string) =>
        sdk.fetchWrapper.get<TeacherTransferInventoryResponse>(`/teachers/${id}/transferInventory`);
    const archiveTeacherWithTransfer = async (id: string, transfers: TeacherTransferAssignment[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<void>(`/teachers/${id}/archiveWithTransfer`, { transfers }, options);
    // #991 — hours moved between competences in one transaction: the inventory the dialog picks
    // from, and the transfer itself. Two scopes: a program's referential, one delivery.
    const getProgramHoursTransferInventory = async (programId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<HoursTransferInventoryResponse>(`/learningPrograms/${programId}/hoursTransfers/inventory`, options);
    const transferProgramHours = async (programId: string, request: HoursTransferRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<HoursTransfer>(`/learningPrograms/${programId}/hoursTransfers`, request, options);
    const getPlanningHoursTransferInventory = async (planningId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<HoursTransferInventoryResponse>(`/plannings/${planningId}/hoursTransfers/inventory`, options);
    const transferPlanningHours = async (planningId: string, request: HoursTransferRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<HoursTransfer>(`/plannings/${planningId}/hoursTransfers`, request, options);
    const restoreTeacher = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${id}/restore`, {}, options);
    const teachersByProgram = async (programId: string) => sdk.fetchWrapper.get<TeachersResponse>(`/learningPrograms/${programId}/teachers/`);
    const getAvailableTeachers = async (
        programId: string,
        options: { frequentationId?: string; date?: string; periodId?: string; moduleIds?: string[] } = {}
    ) => {
        const params = new URLSearchParams({ programId });
        if (options.frequentationId) params.set('frequentationId', options.frequentationId);
        if (options.date) params.set('date', options.date);
        if (options.periodId) params.set('periodId', options.periodId);
        if (options.moduleIds?.length) params.set('moduleIds', options.moduleIds.join(','));
        return sdk.fetchWrapper.get<AvailableTeachersResponse>(`/teachers/available?${params.toString()}`);
    };
    const addTeacher = async (userId: string) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/`, { userId });

    // Vacancies / hires (#495)
    const getVacancies = async () => sdk.fetchWrapper.get<VacanciesResponse>(`/teachers/vacancies`);
    const createVacancy = async (data: CreateVacancyRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/vacancies`, data, options);
    const archiveVacancy = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/vacancies/${id}/archive`, {}, options);
    const hireVacancy = async (id: string, userId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/vacancies/${id}/hire`, { userId }, options);
    const renameVacancy = async (id: string, label: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/vacancies/${id}/rename`, { label }, options);
    const addTeachersFromEntraIdGroup = async (groupId: string) => await sdk.fetchWrapper.post<TeachersResponse>(`/teachers/FromEntraIdGroup`, { groupId });
    const archiveTeacher = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/${id}/archive`, {}, options);
    // Pass null to clear the custom colour and fall back to the email-derived default.
    const setTeacherColor = async (id: string, color: string | null, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/teachers/${id}/color`, { color }, options);
    // « Type d'intervenant » and « Local de rencontre » (#1226), saved together: with a type, the
    // teacher is offered wherever a period is booked « avec un spécialiste ». Null or blank clears the
    // type, and the room with it; longer than 120 characters is a 400 sentence. The room must be a
    // live centre room (a stored one archived since is kept); a vacancy is refused.
    const setTeacherSpecialistType = async (id: string, specialistType: string | null, specialistRoomId: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${id}/specialistType`, { specialistType, specialistRoomId }, options);

    // Teacher-LearningProgram relationship functions
    const getTeacherLearningPrograms = async (teacherId: string, options?: SdkRequestOptions) => sdk.fetchWrapper.get<LearningProgramsResponse>(`/teachers/${teacherId}/learningPrograms/`, options);
    const assignTeacherToProgram = async (teacherId: string, programId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<void>(`/teachers/${teacherId}/learningPrograms/${programId}`, {}, options);
    const removeTeacherFromProgram = async (teacherId: string, programId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/teachers/${teacherId}/learningPrograms/${programId}`, options);

    // Teacher-LearningModule relationship functions (module affinity)
    const getTeacherLearningModules = async (teacherId: string, options?: SdkRequestOptions) => sdk.fetchWrapper.get<LearningModulesResponse>(`/teachers/${teacherId}/learningModules/`, options);
    // archivedModuleIds: the archived competences a group still follows that the teacher gives; left out, the server keeps them.
    const setTeacherProgramModules = async (teacherId: string, programId: string, moduleIds: string[], options?: SdkRequestOptions, archivedModuleIds?: string[]) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${teacherId}/learningPrograms/${programId}/modules`,
            archivedModuleIds ? { moduleIds, archivedModuleIds } : { moduleIds }, options);

    // Teacher availability (#923). The working frame is the Fondation's (Core.Availability); this
    // route resolves it for a teacher on the server, so a teacher reading their own load needs no
    // Fondation permission. Silent handler: the caller words the failure.
    const getTeacherUnavailableRanges = (teacherId: string, from: string, to: string) =>
        sdk.fetchWrapper.get<{ ranges: UnavailableRange[] }>(
            `/teachers/${teacherId}/unavailableRanges?from=${from}&to=${to}`, { errorHandler: null });
    // The frame itself (validity, weekly windows, absences), in the Fondation's own shape.
    const getTeacherWorkAvailability = (teacherId: string) =>
        sdk.fetchWrapper.get<UserAvailability>(`/teachers/${teacherId}/workAvailability`, { errorHandler: null });
    const getTeacherAvailabilities = async (teacherId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<TeacherAvailabilitiesResponse>(`/teachers/${teacherId}/availabilities`, options);
    const createTeacherAvailability = async (teacherId: string, request: TeacherAvailabilityRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<TeacherAvailabilityResponse>(`/teachers/${teacherId}/availabilities`, request, options);
    const updateTeacherAvailability = async (teacherId: string, availabilityId: string, request: TeacherAvailabilityRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<TeacherAvailabilityResponse>(`/teachers/${teacherId}/availabilities/${availabilityId}`, request, options);
    // « Prolonger » (#971): the end alone, null for none. The update above rewrites the window from
    // what is sent, and a legacy window cannot be sent back as it is stored.
    const setTeacherAvailabilityEndDate = async (teacherId: string, availabilityId: string, endDate: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<TeacherAvailabilityResponse>(`/teachers/${teacherId}/availabilities/${availabilityId}/endDate`, { endDate }, options);
    const deleteTeacherAvailability = async (teacherId: string, availabilityId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/teachers/${teacherId}/availabilities/${availabilityId}`, options);

    // Frequentation API functions
    const frequentations = async () => sdk.fetchWrapper.get<FrequentationsResponse>(`/frequentations/`);
    const myFrequentations = async () => sdk.fetchWrapper.get<FrequentationsResponse>(`/frequentations/mine`);
    const myFrequentationPresences = async (frequentationId: string, startDate: string, endDate: string) =>
        sdk.fetchWrapper.get<PresencesByStudentResponse>(`/frequentations/mine/${frequentationId}/presences?startDate=${startDate}&endDate=${endDate}`);
    const myFrequentationLearningModules = async (frequentationId: string) =>
        sdk.fetchWrapper.get<LearningModulesResponse>(`/frequentations/mine/${frequentationId}/learningModules`);
    const myFrequentationModulePlans = async (frequentationId: string, startDate: string, endDate: string) =>
        sdk.fetchWrapper.get<ModulePlansBatchResponse>(`/frequentations/mine/${frequentationId}/modulePlans?startDate=${startDate}&endDate=${endDate}`);
    const generateMyStudentScheduleReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/scheduleReport`, {}, options);
    // The dated calendar (#961), read over a window — unlike the schedule above, which is the
    // weekly cycle and needs no dates. « year » prints twelve months from the start date and
    // ignores the end date (#1095).
    const generateMyStudentCalendarReport = async (frequentationId: string, startDate: string, endDate: string, layout: 'monthly' | 'year' = 'monthly', paper: 'letter' | 'legal' = 'letter', options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/calendarReport`, { startDate, endDate: endDate || startDate, layout, paper }, options);
    // The two nominative reports a student may ask about themselves. The enrolment is named by the
    // route, never by the body — the ownership guard reads it there.
    const generateMyProgressionReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/progressionReport`, {}, options);
    const generateMyAbsenceReport = async (frequentationId: string, startDate: string, endDate: string, options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost(`/frequentations/mine/${frequentationId}/absenceReport`, { startDate, endDate }, options);
    // `blockExams`: the cells the write marks as the exam of a whole block (#1453), whose rows then
    // make one exam of the block on the server rather than one per member.
    const myBulkUpsertPresences = async (frequentationId: string, presences: Partial<Presence>[], presenceIdsToDelete: string[] = [], blockExams: BlockExamCell[] = [], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ createdCount: number; updatedCount: number; deletedCount: number }>(
            `/frequentations/mine/${frequentationId}/presences/bulk`,
            { presences, presenceIdsToDelete, blockExams }, options);
    const getFrequentationById = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<FrequentationResponse>(`/frequentations/${id}`, options);

    // ── Schedule personalization (calendar v3, #592) ─────────────────────────
    // Owner = frequentation or planning; the mutations go through the Parcours orchestration,
    // which refuses upstream any operation touching an already recorded day.
    /** Route segment per chain-owner kind (mirror of `ScheduleChainOwnerKind`). */
    const scheduleOwnerPath: Record<ScheduleChainOwnerType, string> = {
        programPhase: 'programPhases',
        planningPhase: 'planningPhases',
    };
    const getScheduleChainInfo = async (ownerType: ScheduleChainOwnerType, ownerId: string) =>
        sdk.fetchWrapper.get<ScheduleChainInfo>(`/${scheduleOwnerPath[ownerType]}/${ownerId}/schedule/chain`);
    const applyScheduleChainOperation = async (
        ownerType: ScheduleChainOwnerType, ownerId: string, operation: ScheduleChainOperation, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<ScheduleChainOperationResult>(
            `/${scheduleOwnerPath[ownerType]}/${ownerId}/schedule/operations`, operation, options);

    // ── Student availability (#1019): held by the Fondation for the student's user, read and written
    // through the pathway — the planner needs no Core.Availability.Manage — then the pathway re-laid.
    const getPlanningAvailability = (planningId: string) =>
        sdk.fetchWrapper.get<UserAvailability>(`/plannings/${planningId}/availability`, { errorHandler: null });
    const createPlanningAvailabilityFrame = (planningId: string, request: AvailabilityFrameRequest) =>
        sdk.fetchWrapper.post<unknown>(`/plannings/${planningId}/availability/frames`, request, { errorHandler: null });
    const updatePlanningAvailabilityFrame = (planningId: string, frameId: string, request: AvailabilityFrameRequest) =>
        sdk.fetchWrapper.put<unknown>(`/plannings/${planningId}/availability/frames/${frameId}`, request, { errorHandler: null });
    const deletePlanningAvailabilityFrame = (planningId: string, frameId: string) =>
        sdk.fetchWrapper.delete<void>(`/plannings/${planningId}/availability/frames/${frameId}`, { errorHandler: null });
    const createPlanningUnavailability = (planningId: string, request: UnavailabilityRequest) =>
        sdk.fetchWrapper.post<unknown>(`/plannings/${planningId}/availability/unavailabilities`, request, { errorHandler: null });
    const updatePlanningUnavailability = (planningId: string, unavailabilityId: string, request: UnavailabilityRequest) =>
        sdk.fetchWrapper.put<unknown>(`/plannings/${planningId}/availability/unavailabilities/${unavailabilityId}`, request, { errorHandler: null });
    const deletePlanningUnavailability = (planningId: string, unavailabilityId: string) =>
        sdk.fetchWrapper.delete<void>(`/plannings/${planningId}/availability/unavailabilities/${unavailabilityId}`, { errorHandler: null });
    /** The assignments (teacher, room, presence type) whose slot the new availability no longer lays. */
    const relayPlanningAvailability = async (planningId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ relaid: number; lost: { date: string; periodName: string }[] }>(
            `/plannings/${planningId}/availability/relay`, {}, options);
    const frequentationsByTeacher = async (teacherId: string, date?: string) => {
        const params = date ? `?date=${date}` : '';
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/teachers/${teacherId}/frequentations/${params}`);
    };
    const frequentationsByProgram = async (programId: string, options?: { excludeGroupMembers?: boolean; siteId?: string }) => {
        const params = new URLSearchParams();
        params.append('excludeGroupMembers', (options?.excludeGroupMembers ?? false).toString());
        // #1339: the toolbar's centre, sent only when one is selected.
        if (options?.siteId) params.append('siteId', options.siteId);
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/learningprograms/${programId}/frequentations/?${params.toString()}`);
    };
    const getStudentById = async (studentId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<Student>(`/students/${studentId}`, options);
    const getStudentByUserId = async (userId: string) =>
        sdk.fetchWrapper.get<Student>(`/students/byUser/${userId}`);
    const frequentationsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/students/${studentId}/frequentations`);
    const addFrequentation = async (
        userId: string,
        learningProgramId: string,
        startDate: string,
        endDate: string | null = null,
        status: string | null = null,
        fundingSource: string | null = null,
        pathType: string | null = null,
        exemptionCode: string | null = null,
        isConcomitant: boolean = false,
        nbHoursFG: number | null = null,
        isSmallGroup: boolean = false,
        /** Calendar family of the individual delivery created with the frequentation
         *  (individual program). Without it, the server reuses the one of the program's
         *  latest delivery, and failing that creates no delivery (#587). */
        calendarId: string | null = null,
        options?: SdkRequestOptions,
        /** The centre the student attends (#1339), for an individualised enrolment. */
        siteId: string | null = null,
        /** The « motif de fin de fréquentation » (#1234), required with an end date. */
        endReason: string | null = null,
    ) => await sdk.fetchWrapper.post<FrequentationResponse>(`/frequentations/`, {
        userId, learningProgramId, startDate, endDate, status, fundingSource, pathType, exemptionCode, isConcomitant, nbHoursFG, isSmallGroup, calendarId, siteId, endReason,
    }, options);
    const addFrequentationsFromEntraIdGroup = async (groupId: string, learningProgramId: string, startDate: string, endDate: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ importedCount: number; skippedCount: number }>(`/frequentations/FromEntraIdGroup`, { groupId, learningProgramId, startDate, endDate }, options);
    const updateFrequentation = async (id: string, startDate: string, endDate: string | null, status: string, fundingSource: string | null, pathType: string | null, exemptionCode: string | null, isConcomitant: boolean, nbHoursFG: number | null, isIndividual: boolean = false, isSmallGroup: boolean = false, defaultExamRoomId: string | null = null, siteId: string | null = null, endReason: string | null = null) => await sdk.fetchWrapper.put<FrequentationResponse>(`/frequentations/${id}`, { startDate, endDate, status, fundingSource, pathType, exemptionCode, isConcomitant, nbHoursFG, isIndividual, isSmallGroup, defaultExamRoomId, siteId, endReason });

    /**
     * The second staff member beside the enseignant-tuteur (#824). Its own route: the screen
     * that carries the field holds none of the enrolment's other values, and
     * `updateFrequentation` replaces them all.
     */
    const updateFrequentationSecondaryStaff = async (id: string, secondaryStaffId: string | null, secondaryStaffRole: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<FrequentationResponse>(`/frequentations/${id}/SecondaryStaff`, { secondaryStaffId, secondaryStaffRole }, options);
    /** Roles already typed in the establishment, for the combobox to offer back. */
    const getSecondaryStaffRoles = async () =>
        await sdk.fetchWrapper.get<{ roles: string[] }>(`/frequentations/SecondaryStaffRoles`);

    const frequentationsPaged = async (query: PagedGridQuery): Promise<PagedFrequentationsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedFrequentationsResponse>(`/frequentations/paged?${params.toString()}`);
    };

    /**
     * Student summary aggregated on the server side (#724): totals (including "without a tutor")
     * plus breakdowns by site, program, group and tutor. No dates — the counters cover the current
     * state of the records, and the consolidated view has no window anyway.
     */
    const getFrequentationSummary = async (options: FrequentationSummaryOptions = {}) => {
        const params = new URLSearchParams();
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        if (options.status) params.set('status', options.status);
        // Boolean: sent as soon as it is DEFINED, never "if it is true" — see
        // getPresenceSummary.
        if (options.isIndividual !== undefined) params.set('isIndividual', String(options.isIndividual));
        const query = params.toString();
        return sdk.fetchWrapper.get<FrequentationSummary>(`/frequentations/summary${query ? `?${query}` : ''}`);
    };

    // Student API functions
    const studentsPaged = async (query: PagedGridQuery): Promise<PagedStudentsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedStudentsResponse>(`/students/paged?${params.toString()}`);
    };

    const addStudent = async (userId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ id: string; user: { id: string; email: string; firstName: string; lastName: string } }>(`/students/`, { userId }, options);

    const archiveStudent = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/archive`, {}, options);

    const restoreStudent = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/restore`, {}, options);

    const updateStudentCoordinates = async (id: string, data: UpdateStudentCoordinatesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/coordinates`, data, options);

    // Student Address functions
    const getStudentAddresses = async (studentId: string): Promise<StudentAddress[]> =>
        await sdk.fetchWrapper.get<StudentAddress[]>(`/students/${studentId}/addresses`);

    const createStudentAddress = async (studentId: string, data: Omit<StudentAddress, 'id' | 'studentId'>, options?: SdkRequestOptions): Promise<StudentAddress> =>
        await sdk.fetchWrapper.post<StudentAddress>(`/students/${studentId}/addresses`, data, options);

    const updateStudentAddress = async (studentId: string, id: string, data: Omit<StudentAddress, 'id' | 'studentId'>, options?: SdkRequestOptions): Promise<void> =>
        await sdk.fetchWrapper.put<void>(`/students/${studentId}/addresses/${id}`, data, options);

    const deleteStudentAddress = async (studentId: string, id: string, options?: SdkRequestOptions): Promise<void> =>
        await sdk.fetchWrapper.delete<void>(`/students/${studentId}/addresses/${id}`, options);

    const searchStudentsWithFrequentation = async (
        query: string,
        options: { programId?: string; groupId?: string; excludeGroupId?: string; onlyWithActiveFrequentation?: boolean; maxResults?: number } = {}
    ): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(options.maxResults ?? DEFAULT_SEARCH_MAX_RESULTS) });
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.excludeGroupId) params.set('excludeGroupId', options.excludeGroupId);
        if (options.onlyWithActiveFrequentation) params.set('onlyWithActiveFrequentation', 'true');
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/searchWithFrequentation?${params.toString()}`);
    };

    const searchStudents = async (query: string, maxResults = DEFAULT_SEARCH_MAX_RESULTS): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(maxResults) });
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/search?${params.toString()}`);
    };

    /** System/Entra accounts the student-add search may offer to enroll — staff excluded server-side (#857). */
    const searchEnrollableUsers = async (query: string, maxResults = 10): Promise<{ users: { id: string; email: string; firstName: string; lastName: string }[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(maxResults) });
        return sdk.fetchWrapper.get<{ users: { id: string; email: string; firstName: string; lastName: string }[] }>(`/students/searchEnrollableUsers?${params.toString()}`);
    };

    // Presence functions
    const addPresence = async (data: PresenceRequest) =>
        await sdk.fetchWrapper.post<PresenceResponse>(`/presences/`, data);

    const updatePresence = async (id: string, data: Partial<Presence>) =>
        await sdk.fetchWrapper.put<void>(`/presences/${id}`, data);

    const deletePresence = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presences/${id}`);

    // `options` lets a caller pass `{ errorHandler: null }` and phrase the refusal itself — the
    // Présences sheet does, because a 403 here has a specific meaning worth explaining (#540/#879).
    // `presences` lists every row the server wrote, with its id: a note is attached to a presence
    // by id, and a row created by this very call has none until the server says so.
    const bulkUpsertPresences = async (presences: Partial<Presence>[], presenceIdsToDelete: string[] = [], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<BulkPresenceResponse>(`/presences/bulk`, { presences, presenceIdsToDelete }, options);

    // ── Group courses (#821) ─────────────────────────────────────────────────
    const getGroupCourses = async (query: GroupCourseListQuery, options?: SdkRequestOptions) => {
        const params = new URLSearchParams();
        params.set('page', String(query.page));
        params.set('pageSize', String(query.pageSize));
        if (query.search) params.set('search', query.search);
        if (query.sortBy) params.set('sortBy', query.sortBy);
        params.set('sortDesc', String(query.sortDesc ?? false));
        if (query.programId) params.set('programId', query.programId);
        if (query.teacherId) params.set('teacherId', query.teacherId);
        return sdk.fetchWrapper.get<GroupCoursePage>(`/groupCourses?${params.toString()}`, options);
    };
    const getGroupCoursesSummary = async (query: Pick<GroupCourseListQuery, 'programId' | 'teacherId'>, options?: SdkRequestOptions) => {
        const params = new URLSearchParams();
        if (query.programId) params.set('programId', query.programId);
        if (query.teacherId) params.set('teacherId', query.teacherId);
        // Not `params.size`: Safari before 17 does not have it.
        const queryString = params.toString();
        const suffix = queryString ? `?${queryString}` : '';
        return sdk.fetchWrapper.get<GroupCourseSummary>(`/groupCourses/summary${suffix}`, options);
    };
    const getGroupCourseDefaults = async (programId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<GroupCourseDefaults>(`/groupCourses/defaults?programId=${encodeURIComponent(programId)}`, options);
    const getGroupCourse = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ course: GroupCourse }>(`/groupCourses/${id}`, options);
    const previewGroupCourse = async (input: GroupCourseInput, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ sessions: GeneratedSession[] }>(`/groupCourses/preview`, input, options);
    const createGroupCourse = async (input: GroupCourseInput, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ course: GroupCourse }>(`/groupCourses`, input, options);
    /**
     * A recurrence edit that would recompute hand-made sessions or archive registrant rows answers
     * 409 with the losses until `confirmRegeneration` is set: that is an outcome the dialog
     * renders, not a fault, so it comes back as the union. Any other error is thrown as usual.
     */
    const updateGroupCourse = async (
        id: string, input: GroupCourseInput, confirmRegeneration = false, options?: SdkRequestOptions,
    ): Promise<UpdateGroupCourseOutcome> => {
        const url = `/groupCourses/${id}${confirmRegeneration ? '?confirmRegeneration=true' : ''}`;
        try {
            const result = await sdk.fetchWrapper.put<GroupCourseUpdateResult>(url, input, { ...options, errorHandler: null });
            return { kind: 'saved', course: result.course, skipped: result.skipped ?? [] };
        } catch (err) {
            if (err instanceof ApiError && err.httpStatus !== undefined) {
                const losses = toRegenerationLosses(err.httpStatus, err.content);
                if (losses) return { kind: 'needsConfirmation', losses };
            }
            throw err;
        }
    };
    const archiveGroupCourse = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/groupCourses/${id}`, options);
    /**
     * Adding or moving a session whose rows would replace unconfirmed assignments answers 409 with
     * the replacements until `confirmReplacements` is set: an outcome the detail renders as a
     * question, not a fault, so it comes back as the union. Any other error is thrown as usual.
     */
    const writeSession = async (
        write: (url: string) => Promise<GroupCourseSessionResult>, url: string, confirmReplacements: boolean,
    ): Promise<SessionWriteOutcome> => {
        try {
            const result = await write(`${url}${confirmReplacements ? '?confirmReplacements=true' : ''}`);
            return { kind: 'saved', session: result.session, skipped: result.skipped ?? [] };
        } catch (err) {
            if (err instanceof ApiError && err.httpStatus !== undefined) {
                const replacements = toSessionReplacements(err.httpStatus, err.content);
                if (replacements) return { kind: 'needsConfirmation', replacements };
            }
            throw err;
        }
    };
    const addGroupCourseSession = async (
        courseId: string, date: string, periodId: string, confirmReplacements = false, options?: SdkRequestOptions,
    ) => writeSession(
        url => sdk.fetchWrapper.post<GroupCourseSessionResult>(url, { date, periodId }, { ...options, errorHandler: null }),
        `/groupCourses/${courseId}/sessions`, confirmReplacements);
    const moveGroupCourseSession = async (
        courseId: string, sessionId: string, date: string, periodId: string, confirmReplacements = false, options?: SdkRequestOptions,
    ) => writeSession(
        url => sdk.fetchWrapper.put<GroupCourseSessionResult>(url, { date, periodId }, { ...options, errorHandler: null }),
        `/groupCourses/${courseId}/sessions/${sessionId}`, confirmReplacements);
    const cancelGroupCourseSession = async (courseId: string, sessionId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/groupCourses/${courseId}/sessions/${sessionId}`, options);

    /**
     * A take answers 200, 409 (rows to replace, needs the user's confirmation) or 400 with
     * per-session refusals. The last two are outcomes the UI renders, not faults: they come back as
     * the union. Anything else (a plain refusal, 403, a fault) is thrown as usual.
     */
    const postTake = async (url: string, body: object): Promise<TakeCourseResult> => {
        try {
            await sdk.fetchWrapper.post<unknown>(url, body, { errorHandler: null });
            return { kind: 'taken' };
        } catch (err) {
            if (err instanceof ApiError && err.httpStatus !== undefined) {
                const outcome = toTakeCourseResult(err.httpStatus, err.content);
                if (outcome) return outcome;
            }
            throw err;
        }
    };
    const takeGroupCourse = async (courseId: string, frequentationId: string, confirmReplacements: boolean): Promise<TakeCourseResult> =>
        postTake(`/groupCourses/${courseId}/registrations`, { frequentationId, confirmReplacements });
    const dropGroupCourse = async (courseId: string, frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/groupCourses/${courseId}/registrations/${frequentationId}/drop`, {}, options);
    const myTakeGroupCourse = async (frequentationId: string, courseId: string, confirmReplacements: boolean): Promise<TakeCourseResult> =>
        postTake(`/frequentations/mine/${frequentationId}/groupCourses/${courseId}/registration`, { confirmReplacements });
    const myDropGroupCourse = async (frequentationId: string, courseId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/frequentations/mine/${frequentationId}/groupCourses/${courseId}/drop`, {}, options);

    const getPresencesByDateRange = async (
        startDate: string, endDate: string, programId?: string, options?: PresenceDateRangeOptions) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (programId) params.append('programId', programId);
        // Sent only when asked: absent, the server keeps its historical reading (#1226).
        if (options?.withSpecialistBookings) params.append('withSpecialistBookings', 'true');
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/presences/dateRange?${params.toString()}`);
    };

    const getTeacherPresencesByDateRange = async (
        teacherId: string, startDate: string, endDate: string, options?: PresenceDateRangeOptions) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (options?.withSpecialistBookings) params.append('withSpecialistBookings', 'true');
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/teachers/${teacherId}/presences/dateRange?${params.toString()}`);
    };

    /**
     * Presence summary aggregated on the server side (#724): totals plus breakdowns by site,
     * program, group and teacher. `from`/`to` are required — every page brings its own window (one
     * day for assiduity and presences, twenty-eight for assignments). The per-teacher scope is
     * applied by the server: a teacher receives THEIR figures.
     */
    const getPresenceSummary = async (options: PresenceSummaryOptions) => {
        const params = new URLSearchParams();
        params.set('from', options.from);
        params.set('to', options.to);
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        // The `if (x)` guard above is right for an optional GUID, but it would swallow `false` on
        // a boolean: the flag is sent as soon as it is DEFINED, never "if it is true". That is
        // exactly the defect that returned a 400 to the work placements page (#723).
        if (options.isIndividual !== undefined) params.set('isIndividual', String(options.isIndividual));
        // The « Activité » filter (#1163), one repeated key per type, for the landing's figures:
        // the day sheet itself loads whole and is filtered on the page.
        for (const key of options.presenceTypes ?? []) params.append('presenceTypes', key);
        return sdk.fetchWrapper.get<PresenceSummary>(`/presences/summary?${params.toString()}`);
    };

    /**
     * Summary of the PLANNED load aggregated on the server side (#724): slots laid down, not
     * recorded presences. `from`/`to` are required — the page brings the school year it displays.
     * The per-teacher scope is applied by the server: a teacher receives THEIR load, and therefore
     * has nothing to select to see their own.
     */
    const getTeacherLoadSummary = async (options: TeacherLoadSummaryOptions) => {
        const params = new URLSearchParams();
        params.set('from', options.from);
        params.set('to', options.to);
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.teacherId) params.set('teacherId', options.teacherId);
        return sdk.fetchWrapper.get<TeacherLoadSummary>(`/plannings/loadSummary?${params.toString()}`);
    };

    /** Who is absent on a day, and what their absence leaves uncovered (#720). */
    const getAbsenceDay = async (date: string) =>
        sdk.fetchWrapper.get<AbsenceDay>(`/plannings/absenceDay?date=${encodeURIComponent(date)}`);

    /** The same read over a span — only the days that actually have somebody absent (#720). */
    const getAbsenceRange = async (from: string, to: string) =>
        sdk.fetchWrapper.get<AbsenceRange>(
            `/plannings/absenceDay?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

    // StudentNote functions (notes belong to the student, #445)
    const getStudentNotesByPresence = async (presenceId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentNote[] }>(`/presences/${presenceId}/notes`);

    const getStudentNotes = async (studentId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentNote[] }>(`/students/${studentId}/notes`);

    const createStudentNoteByPresence = async (presenceId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentNote>(`/presences/${presenceId}/notes`, { noteType, level, message });

    const createStudentNote = async (studentId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentNote>(`/students/${studentId}/notes`, { noteType, level, message });

    const updateStudentNote = async (id: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.put<void>(`/studentNotes/${id}`, { noteType, level, message });

    const deleteStudentNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/studentNotes/${id}`);

    // StudentSharedNote functions (#822) — the student-visible follow-up space. Staff routes
    // are student-scoped like the staff notes; the student reads through /Mine only.
    const getStudentSharedNotes = async (studentId: string) =>
        sdk.fetchWrapper.get<{ notes: StudentSharedNote[] }>(`/students/${studentId}/sharedNotes`);

    const createStudentSharedNote = async (studentId: string, level: string, message: string) =>
        sdk.fetchWrapper.post<StudentSharedNote>(`/students/${studentId}/sharedNotes`, { level, message });

    const updateStudentSharedNote = async (id: string, level: string, message: string) =>
        sdk.fetchWrapper.put<void>(`/studentSharedNotes/${id}`, { level, message });

    const deleteStudentSharedNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/studentSharedNotes/${id}`);

    const mySharedNotes = async () =>
        sdk.fetchWrapper.get<{ notes: StudentSharedNote[] }>(`/studentSharedNotes/mine`);

    // Staff notes on an exam (#1452), behind their own two permissions (PERMISSION_EXAM_NOTES_*).
    // `options` so the dialog can pass `{ errorHandler: null }` and say the server's refusal itself.
    const getExamNoteCounts = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<ExamNoteCountsResponse>(`/examNotes/counts`, options);

    const getExamNotes = async (examId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<ExamNotesResponse>(`/exams/${examId}/notes`, options);

    /** `wholeSitting`: one copy on each exam of the cohort sitting, edited and deleted together. */
    const createExamNote = async (examId: string, message: string, wholeSitting: boolean, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<ExamNote>(`/exams/${examId}/notes`, { message, wholeSitting }, options);

    const updateExamNote = async (id: string, message: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/examNotes/${id}`, { message }, options);

    const deleteExamNote = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/examNotes/${id}`, options);

    // Exam functions
    const exams = async () => sdk.fetchWrapper.get<ExamsResponse>(`/exams/`);

    function buildExamFilterParams(filters: ExamFilters): URLSearchParams {
        const params = new URLSearchParams();
        if (filters.programId) params.append('programId', filters.programId);
        if (filters.groupId) params.append('groupId', filters.groupId);
        if (filters.moduleId) params.append('moduleId', filters.moduleId);
        if (filters.frequentationId) params.append('frequentationId', filters.frequentationId);
        if (filters.teacherId) params.append('teacherId', filters.teacherId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.result) params.append('result', filters.result);
        if (filters.studentFilter) params.append('studentFilter', filters.studentFilter);
        return params;
    }

    const examsFiltered = async (filters: ExamFilters) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ exams: ExamListItem[] }>(`/exams/?${params.toString()}`);
    };

    const myExams = async (filters: Pick<ExamFilters, 'moduleId' | 'dateFrom' | 'dateTo' | 'result'>) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ exams: ExamListItem[] }>(`/exams/mine?${params.toString()}`);
    };

    const examsCount = async (filters: ExamFilters) => {
        const params = buildExamFilterParams(filters);
        return sdk.fetchWrapper.get<{ count: number }>(`/exams/count?${params.toString()}`);
    };

    const getExam = async (id: string) => sdk.fetchWrapper.get<ExamResponse>(`/exams/${id}`);

    const getExamsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<ExamsResponse>(`/frequentations/${frequentationId}/exams`);

    const createExam = async (data: ExamRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<ExamResponse>(`/exams/`, data, options);

    /** The group flow: one sitting for the cohort's roster, one exam per member, all or nothing. */
    const createGroupExams = async (groupId: string, data: GroupExamRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<ExamsResponse>(`/groups/${groupId}/exams`, data, options);

    /** The service type proposed for each student of a sitting, and why (#1233). A read, posted for its body. */
    const examServiceTypeSuggestions = async (data: ExamServiceTypeSuggestionRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<ExamServiceTypeSuggestionsResponse>(`/exams/serviceTypeSuggestions`, data, options);

    const updateExam = async (id: string, data: ExamUpdateRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/exams/${id}`, data, options);

    const deleteExam = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/exams/${id}`, options);

    // The exam supervisor's day sheet (#1164): the day's sittings of every program, in the shape
    // of the teacher's day sheet so Présences renders it as is — names and the sitting, never the
    // student file. Both routes 404 while the ExamSupervision flag is off. Never cached offline.
    const getExamSupervisionDaySheet = async (date: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<ExamSupervisionDaySheet>(`/ExamSupervision/DaySheet?date=${encodeURIComponent(date)}`, options);

    // All or nothing: one refused entry and nothing is written; each entry writes every period of
    // its exam's span. The refusal is a French sentence (400), so the page passes
    // `{ errorHandler: null }` and relays it (see utils/api-error). Answers the fresh day sheet of
    // that date, which the page repaints from.
    const recordExamSupervisionPresences = async (date: string, entries: ExamSupervisionEntry[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<ExamSupervisionDaySheet>(`/ExamSupervision/Presences`, { date, entries } satisfies ExamSupervisionPresencesRequest, options);

    // The specialist's follow-up (#1226): the caller's own followed students — the server decides
    // who that is — every live student, one student, their schedule, and the periods booked « avec
    // un spécialiste ». Every route answers 404 while SpecialistView is off; a student the caller
    // may not read answers a bodiless 403. Never cached (Cache-Control: no-store, vite.config.ts).
    // Pages pass `{ errorHandler: null }` and say why themselves; a booking refusal is a French
    // sentence (400) they relay.
    const getMyFollowedStudents = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistStudentsResponse>('/FollowedStudents/Mine', options);
    const getSpecialistDirectory = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistDirectoryResponse>('/SpecialistStudents', options);
    const getSpecialistStudent = async (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistStudent>(`/SpecialistStudents/${encodeURIComponent(frequentationId)}`, options);
    const getSpecialistStudentSchedule = async (frequentationId: string, from: string, to: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistSchedule>(
            `/SpecialistStudents/${encodeURIComponent(frequentationId)}/Schedule?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, options);
    const getSpecialistBookingRooms = async (frequentationId: string, query: SpecialistBookingRoomsQuery, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistBookingRooms>(
            `/SpecialistStudents/${encodeURIComponent(frequentationId)}/BookingRooms?date=${encodeURIComponent(query.date)}`
            + `&periodId=${encodeURIComponent(query.periodId)}`
            + (query.specialistId ? `&specialistId=${encodeURIComponent(query.specialistId)}` : ''), options);
    const bookSpecialistPeriod = async (frequentationId: string, request: SpecialistBookingRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/SpecialistStudents/${encodeURIComponent(frequentationId)}/Bookings`, request, options);
    const cancelSpecialistPeriod = async (frequentationId: string, date: string, periodId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(
            `/SpecialistStudents/${encodeURIComponent(frequentationId)}/Bookings?date=${encodeURIComponent(date)}&periodId=${encodeURIComponent(periodId)}`, options);
    const getSpecialists = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistsResponse>('/Specialists', options);
    // The specialist's own section and its management, Gestion › Spécialistes (#1445).
    const getMySpecialistDaySheet = async (date: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistDaySheetResponse>(`/Specialists/Me/DaySheet?date=${encodeURIComponent(date)}`, options);
    const recordMySpecialistPresences = async (request: SpecialistPresencesRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<SpecialistDaySheetResponse>('/Specialists/Me/Presences', request, options);
    const getMySpecialistAgenda = async (from: string, to: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistAgenda>(
            `/Specialists/Me/Bookings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, options);
    const getMySpecialistGroups = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistGroupsResponse>('/Specialists/Me/Groups', options);
    const getMySpecialistGroupAttendance = async (groupId: string, from: string, to: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistGroupAttendance>(
            `/Specialists/Me/Groups/${encodeURIComponent(groupId)}/Attendance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, options);
    const getMySpecialistGroupProgression = async (groupId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistGroupProgression>(`/Specialists/Me/Groups/${encodeURIComponent(groupId)}/Progression`, options);
    const getSpecialistRoster = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistRosterResponse>('/Specialists/Roster', options);
    const getSpecialistCandidates = async (search: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<SpecialistCandidatesResponse>(`/Specialists/Candidates?search=${encodeURIComponent(search)}`, options);
    const registerSpecialist = async (request: RegisterSpecialistRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<SpecialistRosterRow>('/Specialists', request, options);

    // Schedule configuration now managed by Fondation Calendar system (sdk.Calendar)

    // Export/Import functions
    const exportLearningData = async (options?: SdkRequestOptions): Promise<Blob> =>
        await sdk.fetchWrapper.download(`/learningData/export`, options);

    const importLearningData = async (file: File, contentType: string = "application/zip", options?: SdkRequestOptions): Promise<ImportResultDto> => {
        return await sdk.fetchWrapper.upload("/learningData/import", file, contentType, options) as unknown as ImportResultDto;
    };

    // Consolidated view function
    const getConsolidatedView = async (programId: string, includeInactive: boolean = false, teacherId?: string, excludeGroupMembers: boolean = false) => {
        const params = new URLSearchParams();
        params.append('programId', programId);
        params.append('includeInactive', includeInactive.toString());
        params.append('excludeGroupMembers', excludeGroupMembers.toString());
        if (teacherId) {
            params.append('teacherId', teacherId);
        }
        return sdk.fetchWrapper.get<ConsolidatedViewResponse>(`/consolidatedView/?${params.toString()}`);
    };

    // Every report generator takes `options` last, so the report forms can relay the server's own
    // refusal instead of the SDK's generic bubble on top of it: a chef asking for a colleague
    // outside their programs (#1083), any refusal the form now shows as its own toast (#1090).

    // Append the toolbar-selected centre so ReportBrandingService can brand the PDF (#487).
    const withSiteId = (path: string, siteId?: string | null) =>
        siteId ? `${path}?siteId=${encodeURIComponent(siteId)}` : path;

    /**
     * A report's window is made of calendar dates (`DateOnly` on the server): the field's
     * `yyyy-MM-dd`, sent as it is. Turned into an instant (`toISOString()`, midnight UTC), it was read
     * in the server's own time zone (`TZ=America/Toronto`), and every window started and ended a day
     * early (#1488).
     */
    const reportDate = (value: string) => /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0] ?? value;

    // Exam assignment report function
    const generateExamAssignmentReport = async (startDate: string, endDate: string, programIds?: string[], studentFilter?: string | null, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: ExamAssignmentReportRequest = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate)
        };

        if (programIds && programIds.length > 0) {
            payload.programIds = programIds;
        }
        if (studentFilter) payload.studentFilter = studentFilter;

        return await sdk.fetchWrapper.downloadPost(withSiteId('/examAssignmentReport/', siteId), payload, options);
    };

    // Absence report function
    const generateAbsenceReport = async (frequentationId: string, startDate: string, endDate: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload = {
            frequentationId,
            startDate: reportDate(startDate),
            endDate: reportDate(endDate)
        };

        return await sdk.fetchWrapper.downloadPost(withSiteId('/absenceReport/', siteId), payload, options);
    };

    // Weekly presence report function
    const generateWeeklyPresenceReport = async (teacherId: string, startDate: string, endDate: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload = {
            teacherId,
            startDate: reportDate(startDate),
            endDate: reportDate(endDate)
        };

        return await sdk.fetchWrapper.downloadPost(withSiteId('/weeklyPresenceReport/', siteId), payload, options);
    };

    const generateProgressionReport = async (frequentationId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/progressionReport/', siteId), { frequentationId }, options);
    };

    const generateResultTransmissionModuleReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/resultTransmissionModuleReport/', { frequentationId }, options);
    };

    const generateResultTransmissionAllModulesReport = async (groupId: string, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/resultTransmissionAllModulesReport/', { groupId }, options);
    };

    const generateDepartureSheetReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/departureSheetReport/', { frequentationId }, options);
    };

    const generateReturnSheetReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/returnSheetReport/', { frequentationId }, options);
    };

    const generateRetakeRefusalReport = async (frequentationId: string, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/retakeRefusalReport/', { frequentationId }, options);
    };

    const generateFrequentationInfoReport = async (frequentationId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/frequentationInfoReport/', siteId), { frequentationId }, options);
    };

    const generateStudentScheduleReport = async (frequentationId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/studentScheduleReport/', siteId), { frequentationId }, options);
    };

    const generateAtRiskReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string, studentFilter?: string | null, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/atRiskReport/', siteId), payload, options);
    };

    const generateModulePerformanceReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, studentFilter?: string | null, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/modulePerformanceReport/', siteId), payload, options);
    };

    const generateTeacherWorkloadReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, studentFilter?: string | null, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherWorkloadReport/', siteId), payload, options);
    };

    const generateTeacherAnnualLoadReport = async (startDate: string, endDate: string, teacherId: string, programId?: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = { startDate, endDate, teacherId };
        if (programId) payload.programId = programId;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherAnnualLoadReport/', siteId), payload, options);
    };

    const generateTeacherAssignmentCalendarReport = async (startDate: string, endDate: string, programId?: string, teacherId?: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate),
        };
        if (programId) payload.programId = programId;
        if (teacherId) payload.teacherId = teacherId;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/teacherAssignmentCalendarReport/', siteId), payload, options);
    };

    /** A teacher's or a group's schedule as a printable calendar (#1142, #1121); « year » ignores the end date. */
    const generateScheduleCalendarReport = async (subject: 'teacher' | 'group', subjectId: string, startDate: string, endDate: string, layout: 'monthly' | 'year', programId?: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate || startDate),
            layout,
        };
        if (subject === 'teacher') payload.teacherId = subjectId;
        else payload.groupId = subjectId;
        if (programId) payload.programId = programId;
        const route = subject === 'teacher' ? '/teacherScheduleCalendarReport/' : '/groupScheduleCalendarReport/';
        return await sdk.fetchWrapper.downloadPost(withSiteId(route, siteId), payload, options);
    };

    const generateCalendarYearReport = async (calendarId: string, calendarYearId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/calendarYearReport/', siteId), {
            calendarId,
            calendarYearId,
        }, options);
    };

    const generateAttendanceTrendReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string, studentFilter?: string | null, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: reportDate(startDate),
            endDate: reportDate(endDate),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        if (studentFilter) payload.studentFilter = studentFilter;
        return await sdk.fetchWrapper.downloadPost(withSiteId('/attendanceTrendReport/', siteId), payload, options);
    };

    const generateCumulativeHoursReport = async (programId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/cumulativeHoursReport/', siteId), { programId }, options);
    };

    const generateWorkStudyTimeReport = async (programId: string, siteId?: string | null, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost(withSiteId('/workStudyTimeReport/', siteId), { programId }, options);
    };

    /**
     * One group's hours given against the MEQ normative duration, and the students under
     * `thresholdPercent` of it (#1236). No `siteId`: the server brands the PDF with the group's centre.
     */
    const generateNormativeHoursReport = async (groupId: string, thresholdPercent: number, options?: SdkRequestOptions): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/normativeHoursReport/', { groupId, thresholdPercent }, options);
    };

    // Student journey function
    const getStudentJourney = async (userId: string) => {
        return sdk.fetchWrapper.get<StudentJourneyResponse>(`/studentJourney/${userId}`, { errorHandler: null });
    };

    // FrequentationModuleResults function
    const getFrequentationModuleResults = async (frequentationId: string) =>
        sdk.fetchWrapper.get<FrequentationModuleResultsResponse>(`/frequentations/${frequentationId}/moduleResults`);

    // FrequentationModuleStatus functions
    const getFrequentationModuleStatuses = async (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ statuses: Array<{ frequentationId: string; learningModuleId: string; status: string }> }>(`/frequentations/${frequentationId}/moduleStatuses`, options);

    // Omitting the credited hours (#992) keeps the ones stored: the key drops out of the body.
    const setModuleStatus = async (frequentationId: string, moduleId: string, status: string, alreadyAccumulatedHours?: number, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`, { status, alreadyAccumulatedHours }, options);

    const deleteModuleStatus = async (frequentationId: string, moduleId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`, options);

    const getMyFrequentationModuleLocks = async (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ lockedModuleIds: string[] }>(`/frequentations/mine/${frequentationId}/moduleLocks`, options);

    const getMyFrequentationModuleHours = async (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<FrequentationModuleHoursResponse>(`/frequentations/mine/${frequentationId}/moduleHours`, options);

    // The competencies no qualified, available teacher can take on that date and period (#971):
    // the picker greys them rather than hiding them.
    const getMyFrequentationModuleAvailability = async (
        frequentationId: string, date: string, periodId: string, options?: SdkRequestOptions,
    ) => {
        const params = new URLSearchParams({ date, periodId });
        return sdk.fetchWrapper.get<FrequentationModuleAvailabilityResponse>(
            `/frequentations/mine/${frequentationId}/moduleAvailability?${params.toString()}`, options);
    };

    // The blocks of the student's own pathway (#1000), named or not: scoped by the enrolment they own,
    // since the route names no « Élève » role on a program id.
    const getMySequenceBlocks = (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ blocks: SequenceBlock[] }>(`/frequentations/mine/${frequentationId}/sequenceBlocks`, options);

    // The staff copy, for every enrolment on screen; an enrolment outside the teacher's scope
    // is simply absent from the answer. The ids travel in the query string and Kestrel refuses
    // a request line past 8 KB — about 150 ids — so the largest grids (every individualised
    // enrolment at once) go out in slices merged back into one answer.
    const SEQUENCE_BLOCKS_SLICE = 100;
    const getSequenceBlocksOfFrequentations = async (frequentationIds: string[], options?: SdkRequestOptions) => {
        const slices: string[][] = [];
        for (let start = 0; start < frequentationIds.length; start += SEQUENCE_BLOCKS_SLICE) {
            slices.push(frequentationIds.slice(start, start + SEQUENCE_BLOCKS_SLICE));
        }
        const answers = await Promise.all(slices.map(ids => {
            const query = new URLSearchParams();
            for (const id of ids) query.append('frequentationIds', id);
            return sdk.fetchWrapper.get<{ byFrequentation: Record<string, SequenceBlock[]> }>(
                `/frequentations/sequenceBlocks?${query.toString()}`, options);
        }));
        const byFrequentation: Record<string, SequenceBlock[]> = {};
        for (const answer of answers) Object.assign(byFrequentation, answer.byFrequentation ?? {});
        return { byFrequentation };
    };

    const getFrequentationModuleLocks = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ lockedModuleIds: string[] }>(`/frequentations/${frequentationId}/moduleLocks`);

    // The staff side of the same rule (#971): over the student's next two weeks, the competencies no
    // teacher can give on some period row, for what the pathway's sequence says beside its padlocks.
    const getFrequentationModuleAvailabilityOutlook = async (frequentationId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<FrequentationModuleAvailabilityOutlook>(
            `/frequentations/${frequentationId}/moduleAvailability/outlook`, options);

    const setFrequentationModuleLock = async (frequentationId: string, moduleId: string, isLocked: boolean, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleLocks/${moduleId}`, { isLocked }, options);

    const setAllFrequentationModuleLocks = async (frequentationId: string, isLocked: boolean, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleLocks`, { isLocked }, options);

    // Module planning functions
    const getModulePlan = async (frequentationId: string, startDate: string, endDate: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        return sdk.fetchWrapper.get<ModulePlanResponse>(`/frequentations/${frequentationId}/modulePlan?${params.toString()}`);
    };

    const getModulePlansBatch = async (frequentationIds: string[], startDate: string, endDate: string) =>
        sdk.fetchWrapper.post<ModulePlansBatchResponse>(`/modulePlans/batch`, { frequentationIds, startDate, endDate });

    /**
     * The schedule axis — periods and holidays — of a batch of enrolments and/or deliveries
     * over a window (#592). The single source every grid reads: the server applies the
     * student-grain precedence (personalized chain, else the phase of the delivery in effect)
     * so no screen has to resolve chains itself.
     */
    const getScheduleAxis = async (request: ScheduleAxisRequest) =>
        sdk.fetchWrapper.post<ScheduleAxisResponse>(`/schedule/axis`, {
            frequentationIds: request.frequentationIds ?? [],
            planningIds: request.planningIds ?? [],
            from: request.from,
            to: request.to,
        });

    const getTeacherModulePlans = async (teacherId: string, date: string) =>
        sdk.fetchWrapper.get<ModulePlansBatchResponse>(`/teachers/${teacherId}/modulePlans?date=${date}`);

    /**
     * The fully-assembled Présences sheet for a teacher on a day: ordered period columns and,
     * per column, the student rows already matched into them. Replaces the client-side stitching
     * of frequentations + presences + module plans (see TeacherDaySheetService). The rows whose
     * competence sits outside the recording teacher's affinities come back flagged « en soutien »
     * (#813).
     */
    const getTeacherDaySheet = async (teacherId: string, date: string, programId?: string, options?: SdkRequestOptions, siteId?: string) => {
        const params = new URLSearchParams();
        params.append('date', date);
        if (programId) params.append('programId', programId);
        // #1339: the toolbar's centre, sent only when it narrows something, as on the program sheet.
        if (siteId) params.append('siteId', siteId);
        return sdk.fetchWrapper.get<TeacherDaySheetResponse>(`/teachers/${teacherId}/daySheet?${params.toString()}`, options);
    };

    /**
     * The same sheet for a whole program with no teacher selected (#813): every student of the
     * program — or of every individualized program under the « Tous les individualisés »
     * pseudo-id (#815) — whatever the teacher. Staff only; the server answers 403 otherwise.
     */
    const getProgramDaySheet = async (
        programId: string, date: string, options?: SdkRequestOptions, siteId?: string,
    ) => {
        const params = new URLSearchParams();
        params.append('date', date);
        // #814: the toolbar's Centre filter. Sent only when it narrows something, so a sheet
        // taken with no centre keeps its URL — and its entry in the offline store, which is
        // keyed on the request.
        if (siteId) params.append('siteId', siteId);
        return sdk.fetchWrapper.get<TeacherDaySheetResponse>(`/learningPrograms/${programId}/daySheet?${params.toString()}`, options);
    };

    /** #817: ONE bell notification inviting the student to choose a competency for every
     *  scheduled period of the day left unchosen. Speaks only — no automatism sits behind it. */
    const notifyUnchosenPeriod = async (frequentationId: string, date: string, periodNames: string[]) => {
        return sdk.fetchWrapper.post<void>(`/frequentations/${frequentationId}/notifyUnchosenPeriod`, {
            date, periodNames,
        });
    };

    const getTeacherDailyReport = async (teacherId: string, date: string, programId?: string) => {
        const params = new URLSearchParams();
        params.append('date', date);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<TeacherDailyReportResponse>(`/teachers/${teacherId}/dailyReport?${params.toString()}`);
    };

    // Director Dashboard functions
    // The management dashboard's four cards all take the toolbar's Centre and Programme. Omitted
    // params keep the whole-school reading, so nothing else that calls these routes changes.
    const dashboardScopeQuery = (programId?: string, siteId?: string) => {
        const params = new URLSearchParams();
        if (programId) params.set('programId', programId);
        if (siteId) params.set('siteId', siteId);
        return params.toString() ? `?${params}` : '';
    };

    const getStudentsWithRecentAbsences = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; absenceCount: number }> }>(`/DirectorDashboard/StudentsWithRecentAbsences${dashboardScopeQuery(programId, siteId)}`);
    };

    const getStudentsExceedingModuleTime = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; moduleNumber: string; moduleTitle: string; moduleCode: string; actualHours: number; expectedHours: number; percentageOfExpected: number }> }>(`/DirectorDashboard/StudentsExceedingModuleTime${dashboardScopeQuery(programId, siteId)}`);
    };

    const getStudentsWithDataIssues = async (programId?: string, siteId?: string) => {
        return sdk.fetchWrapper.get<{
            groupMembershipIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string; groupCount: number }>;
            missingTeacherIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string }>;
            /** Groups whose planning is over while students are still « En cours » (#1434). */
            endedPlanningGroups: Array<{ groupId: string; groupName: string; programTitle: string; lastPeriodDate: string; openFrequentationCount: number }>;
        }>(`/DirectorDashboard/StudentsWithDataIssues${dashboardScopeQuery(programId, siteId)}`);
    };

    const getCssPresenceStats = async (programId?: string, siteId?: string) => {
        const query = dashboardScopeQuery(programId, siteId);
        return sdk.fetchWrapper.get<{ presenceStats: Record<string, number> }>(`/DirectorDashboard/PresenceStats${query}`);
    };

    // Planning API functions
    const getPlannings = async () => sdk.fetchWrapper.get<PlanningsResponse>(`/plannings/`);
    const getPlanning = async (id: string) => sdk.fetchWrapper.get<PlanningResponse>(`/plannings/${id}`);
    const createPlanning = async (data: CreatePlanningRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<PlanningResponse>(`/plannings/`, data, options);
    const updatePlanning = async (id: string, data: UpdatePlanningRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/plannings/${id}`, data, options);
    // The end bound alone (PARCOURS-82), null for none: the update above replaces the whole delivery
    // and re-seeds its slots, while a bound moves nothing — and its callers need not carry it.
    const setPlanningEndDate = async (id: string, endDate: string | null, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<PlanningResponse>(`/plannings/${id}/endDate`, { endDate }, options);
    // The best case the end bound may not precede: the last day of the delivery's default layout.
    const getPlanningEarliestEndDate = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<{ earliestEndDate: string | null }>(`/plannings/${id}/earliestEndDate`, options);
    const deletePlanning = async (id: string) => await sdk.fetchWrapper.delete<void>(`/plannings/${id}`);

    // Group API functions
    const groups = async () => sdk.fetchWrapper.get<GroupsResponse>(`/groups/`);
    const getActiveGroups = async () => sdk.fetchWrapper.get<ActiveGroupsResponse>(`/groups/active`);
    const getGroup = async (id: string) => sdk.fetchWrapper.get<GroupResponse>(`/groups/${id}`);
    const createGroup = async (data: CreateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupResponse>(`/groups/`, data, options);
    const duplicateGroup = async (id: string, data: DuplicateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupResponse>(`/groups/${id}/duplicate`, data, options);
    const updateGroup = async (id: string, data: UpdateGroupRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/groups/${id}`, data, options);
    const deleteGroup = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/groups/${id}`, options);

    // Site (Centre) API functions
    const getSites = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ sites: Site[] }>(`/sites/${includeArchived ? '?includeArchived=true' : ''}`);
    const getSite = async (id: string) => sdk.fetchWrapper.get<{ site: Site }>(`/sites/${id}`);
    const createSite = async (data: CreateSiteRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<{ site: Site }>(`/sites/`, data, options);
    const updateSite = async (data: UpdateSiteRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/sites/`, data, options);
    const setProgramSites = async (programId: string, siteIds: string[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/sites`, { siteIds }, options);
    // GRICS imports (#989) — a run is started, then followed: the POST answers at once with the
    // run's record and the work goes on server-side, readable by getGricsImportRun from any tab.
    // A preview is a run too (dryRun), for the same GRICS sweep takes as long. The confirmation
    // before a real import lives on the imports page, not here.
    const getGricsImportAvailability = async (options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<{ configured: boolean }>(`/gricsImport/availability`, options);
    const getGricsImportOverview = async (options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<GricsImportKindOverview[]>(`/gricsImport/runs`, options);
    const getGricsImportRun = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.get<GricsImportRun>(`/gricsImport/runs/${id}`, options);
    const startGricsImportRun = async (request: StartGricsImportRunRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<GricsImportRun>(`/gricsImport/runs`, request, options);
    const cancelGricsImportRun = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<void>(`/gricsImport/runs/${id}/cancel`, {}, options);

    // Room API functions
    // The centres' rooms unless `scope` says otherwise (#1165): every caller but the exam-room
    // pickers wants a room a course can be taught in.
    const getRooms = async (includeArchived = false, scope?: RoomScope, options?: SdkRequestOptions) => {
        const query = new URLSearchParams();
        if (includeArchived) query.set('includeArchived', 'true');
        if (scope) query.set('scope', scope);
        const search = query.toString();
        return sdk.fetchWrapper.get<{ rooms: Room[] }>(`/rooms/${search ? `?${search}` : ''}`, options);
    };
    const getRoomsBySite = async (siteId: string) => sdk.fetchWrapper.get<{ rooms: Room[] }>(`/sites/${siteId}/rooms`);
    /** An enterprise's rooms (#1165), where an exam can be sat. */
    const getRoomsByEnterprise = async (enterpriseId: string) =>
        sdk.fetchWrapper.get<{ rooms: Room[] }>(`/ateEnterprises/${enterpriseId}/rooms`);
    /**
     * The enterprises a room can belong to, archived ones included (#1166): the owner picker of
     * « Lieux d'examen », under the Locations permissions so that page needs no ATE permission.
     */
    const getRoomOwnerEnterprises = async (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ enterprises: RoomOwnerEnterprise[] }>(`/rooms/enterprises`, options);
    const getRoom =async (id: string) => sdk.fetchWrapper.get<{ room: Room }>(`/rooms/${id}`);
    const createRoom = async (data: CreateRoomRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ room: Room }>(`/rooms/`, data, options);
    const updateRoom = async (data: UpdateRoomRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/rooms/`, data, options);
    // #777: read BEFORE archiving, so the confirmation names what it commits to — the upcoming
    // courses and, since exams moved onto rooms (#1166), the upcoming exams.
    const getRoomArchiveImpact = async (id: string) =>
        sdk.fetchWrapper.get<ArchiveImpactResponse>(`/rooms/${id}/archiveImpact`);
    // #1168: who the room already holds while an exam would be sat in it — a warning in the
    // exam dialogs, never a refusal. A read sent as a POST: a batch edit's spans and students
    // do not fit in a URL.
    const getRoomOccupancy = async (id: string, sitting: RoomOccupancyQuery, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<RoomOccupancy>(`/rooms/${id}/occupancy`, sitting, options);

    // Resource API functions
    const getResources = async (includeArchived = false) =>
        sdk.fetchWrapper.get<{ resources: Resource[] }>(`/resources/${includeArchived ? '?includeArchived=true' : ''}`);
    const getResourcesBySite = async (siteId: string) => sdk.fetchWrapper.get<{ resources: Resource[] }>(`/sites/${siteId}/resources`);
    const getResource = async (id: string) => sdk.fetchWrapper.get<{ resource: Resource }>(`/resources/${id}`);
    const createResource = async (data: CreateResourceRequest) => await sdk.fetchWrapper.post<{ resource: Resource }>(`/resources/`, data);
    const updateResource = async (data: UpdateResourceRequest) => await sdk.fetchWrapper.put<void>(`/resources/`, data);
    const setResourcePrograms = async (id: string, data: SetResourceProgramsRequest) => await sdk.fetchWrapper.put<void>(`/resources/${id}/programs`, data);

    /**
     * The raw multipart uploads below bypass fetchWrapper, so they fail the way it does (#1090):
     * an ApiError carrying the status and the parsed body, or — when no answer came — what fetch
     * threw, so that callers read the reason through serverMessage(). They used to throw a plain
     * Error of `body.error ?? statusText`, which put « Failed to fetch » or « Internal Server
     * Error » on screen verbatim, and a 5xx body too.
     */
    const fetchMultipart = async (url: string, init: RequestInit): Promise<string> => {
        let response: Response;
        let text: string;
        try {
            response = await fetch(url, init);
            text = await response.text();
        } catch (err) {
            throw new ApiError(undefined, 'network error', err);
        }
        if (!response.ok) {
            let body: unknown = text;
            try { body = text ? JSON.parse(text) : null; } catch { /* keep the raw text */ }
            throw new ApiError(response.status, response.statusText, body);
        }
        return text;
    };

    // Shared authenticated multipart upload for logos — mirrors uploadRosterFile's auth handling
    // (fetchWrapper.upload throws for OIDC providers).
    const uploadLogoMultipart = async (path: string, file: File): Promise<void> => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const headers: Record<string, string> = {};
        const init: RequestInit = { method: 'POST', headers, body: formData };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        await fetchMultipart(`${apiUrl}${path}`, init);
    };
    // Per-centre logo (#487)
    const getSiteLogo = async (id: string) =>
        sdk.fetchWrapper.get<{ dataUrl: string | null }>(`/sites/${id}/logo`, { errorHandler: null });
    const uploadSiteLogo = (id: string, file: File) => uploadLogoMultipart(`/sites/${id}/logo`, file);
    const deleteSiteLogo = async (id: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/sites/${id}/logo`, options);

    // FrequentationTag API functions
    const getFrequentationTags = async () => sdk.fetchWrapper.get<FrequentationTagsResponse>(`/frequentationTags`);
    const createFrequentationTag = async (data: CreateFrequentationTagRequest) =>
        await sdk.fetchWrapper.post<{ tag: FrequentationTag }>(`/frequentationTags`, data);
    const updateFrequentationTag = async (id: string, data: UpdateFrequentationTagRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${id}`, data);
    const deleteFrequentationTag = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${id}`, options);
    const addFrequentationTagValue = async (tagId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.post<{ id: string; name: string }>(`/frequentationTags/${tagId}/values`, data);
    const updateFrequentationTagValue = async (tagId: string, valueId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${tagId}/values/${valueId}`, data);
    const deleteFrequentationTagValue = async (tagId: string, valueId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${tagId}/values/${valueId}`);
    const getTagsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<FrequentationTagAssignmentsResponse>(`/students/${studentId}/tags`);
    const setStudentTags = async (studentId: string, valueIds: string[], options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/students/${studentId}/tags`, { valueIds }, options);

    // FrequentationNoteTypeDefinition API functions
    const getFrequentationNoteTypeDefinitions = async () =>
        sdk.fetchWrapper.get<FrequentationNoteTypeDefinitionsResponse>(`/frequentationNoteTypeDefinitions`);
    const createFrequentationNoteTypeDefinition = async (data: CreateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<FrequentationNoteTypeDefinition>(`/frequentationNoteTypeDefinitions`, data);
    const updateFrequentationNoteTypeDefinition = async (id: string, data: UpdateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/${id}`, data);
    const deleteFrequentationNoteTypeDefinition = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationNoteTypeDefinitions/${id}`, options);
    const reorderFrequentationNoteTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/Reorder`, { orderedIds });

    // PresenceTypeDefinition API functions
    const getPresenceTypeDefinitions = async () =>
        sdk.fetchWrapper.get<PresenceTypeDefinitionsResponse>(`/presenceTypeDefinitions`);
    const createPresenceTypeDefinition = async (data: CreatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<PresenceTypeDefinition>(`/presenceTypeDefinitions`, data);
    const updatePresenceTypeDefinition = async (id: string, data: UpdatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/${id}`, data);
    const deletePresenceTypeDefinition = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/presenceTypeDefinitions/${id}`, options);
    const reorderPresenceTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/Reorder`, { orderedIds });

    // PresenceStatusDefinition API functions
    const getPresenceStatusDefinitions = async () =>
        sdk.fetchWrapper.get<PresenceStatusDefinitionsResponse>(`/presenceStatusDefinitions`);
    const createPresenceStatusDefinition = async (data: CreatePresenceStatusDefinitionRequest) =>
        await sdk.fetchWrapper.post<PresenceStatusDefinition>(`/presenceStatusDefinitions`, data);
    const updatePresenceStatusDefinition = async (id: string, data: UpdatePresenceStatusDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/presenceStatusDefinitions/${id}`, data);
    const deletePresenceStatusDefinition = async (id: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/presenceStatusDefinitions/${id}`, options);
    const reorderPresenceStatusDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/presenceStatusDefinitions/Reorder`, { orderedIds });

    // Group Membership API functions
    const getGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<GroupMembersResponse>(`/groups/${groupId}/members`);
    const getAvailableGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<FrequentationsResponse>(`/groups/${groupId}/availableMembers`);
    const addGroupMember = async (groupId: string, frequentationId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.post<GroupMemberResponse>(`/groups/${groupId}/members`, { frequentationId }, options);
    const removeGroupMember = async (groupId: string, frequentationId: string, options?: SdkRequestOptions) => await sdk.fetchWrapper.delete<void>(`/groups/${groupId}/members/${frequentationId}`, options);
    // The SDK's fetchWrapper.upload() unconditionally calls getApiAccessToken(), which throws
    // for OIDC providers. We replicate the auth pattern the fetch wrapper applies
    // (credentials: 'include' for OIDC, Bearer/Token for the others) and post FormData
    // so the backend can read it via HttpRequest.ReadFormAsync().
    const postMultipartFile = async (path: string, file: File, fields?: Record<string, string | number | boolean | null | undefined>): Promise<string> => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (fields) {
            for (const [key, value] of Object.entries(fields)) {
                if (value !== null && value !== undefined) formData.append(key, String(value));
            }
        }
        const headers: Record<string, string> = {};
        const init: RequestInit = { method: 'POST', headers, body: formData };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        return await fetchMultipart(`${apiUrl}${path}`, init);
    };
    const uploadRosterFile = async (path: string, file: File, fields?: Record<string, string | number | boolean | null | undefined>): Promise<JadeRosterImportResultDto> => {
        const text = await postMultipartFile(path, file, fields);
        return text ? JSON.parse(text) as JadeRosterImportResultDto : { totalRows: 0, membersAdded: 0, membersAlreadyExisting: 0, errors: [] };
    };
    // The results of a Jade (MEQ) extraction (#1337): one file across students and programmes.
    const importJadeResults = async (file: File): Promise<JadeResultsImportResultDto> => {
        const text = await postMultipartFile('/frequentations/importJadeResults', file);
        return text ? JSON.parse(text) as JadeResultsImportResultDto : { rows: [], totalRows: 0, imported: 0, alreadyPresent: 0, rejected: 0 };
    };
    // The frequentations of a Charlemagne extraction made in Jade (#1368); `dryRun` previews them.
    const importJadeFrequentations = async (file: File, dryRun: boolean, calendarId?: string | null): Promise<JadeFrequentationsImportResultDto> => {
        const params = new URLSearchParams({ dryRun: String(dryRun) });
        if (calendarId) params.set('calendarId', calendarId);
        const text = await postMultipartFile(`/frequentations/importJadeFrequentations?${params.toString()}`, file);
        return text
            ? JSON.parse(text) as JadeFrequentationsImportResultDto
            : { dryRun, rows: [], totalRows: 0, created: 0, updated: 0, unchanged: 0, rejected: 0, studentsCreated: 0 };
    };
    const importJadeRoster = (groupId: string, file: File): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/groups/${groupId}/members/importJadeRoster`, file);
    const importAdmissionFpRoster = (groupId: string, file: File): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/groups/${groupId}/members/importAdmissionFpRoster`, file);
    const importIndividualJadeRoster = (programId: string, file: File, defaults: IndividualRosterImportDefaults): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/learningPrograms/${programId}/importIndividualJadeRoster`, file, { ...defaults });
    const importIndividualAdmissionFpRoster = (programId: string, file: File, defaults: IndividualRosterImportDefaults): Promise<JadeRosterImportResultDto> =>
        uploadRosterFile(`/learningPrograms/${programId}/importIndividualAdmissionFpRoster`, file, { ...defaults });

    // ProgramModuleSequence API functions.
    // Program-level GET is a read-only aggregate across all phases (solver / display).
    const getProgramModuleSequences = async (programId: string) => sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/learningPrograms/${programId}/moduleSequences`);
    // Commit all affected phase snapshots together when fragmenting or moving a competency.
    const updateProgramModuleSequences = async (programId: string, data: UpdateProgramModuleSequencesBatchRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/moduleSequences`, data, options);
    // Sequences are edited per phase.
    const getProgramPhaseModuleSequences = async (programId: string, phaseId: string) => sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/learningPrograms/${programId}/phases/${phaseId}/moduleSequences`);
    const updateProgramPhaseModuleSequences = async (programId: string, phaseId: string, data: UpdateProgramModuleSequencesRequest, options?: SdkRequestOptions) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/phases/${phaseId}/moduleSequences`, data, options);

    // ProgramPhase API functions
    const getProgramPhases = async (programId: string) => sdk.fetchWrapper.get<ProgramPhasesResponse>(`/learningPrograms/${programId}/phases`);
    // Like their delivery-side twins, both template-phase writes refuse with a message in the
    // body (409): the caller passes `{ errorHandler: null }` to silence the SDK's generic bubble
    // and relay that message (see utils/api-error).
    const updateProgramPhases = async (programId: string, data: UpdateProgramPhasesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/phases`, data, options);
    // Deletes one phase; when moveSequencesToPhaseId is given, the phase's module
    // sequences are moved to that phase first (atomically, server-side).
    const deleteProgramPhase = async (programId: string, phaseId: string, moveSequencesToPhaseId?: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/learningPrograms/${programId}/phases/${phaseId}${moveSequencesToPhaseId ? `?moveSequencesToPhaseId=${moveSequencesToPhaseId}` : ''}`, options);

    const getProgramGroupMembers = async (programId: string) => sdk.fetchWrapper.get<GroupMembersByProgramResponse>(`/learningPrograms/${programId}/groupMembers`);

    // Group stats
    const getGroupStats = async (groupId: string) => sdk.fetchWrapper.get<GroupStatsResponse>(`/groups/${groupId}/stats`);
    const getGroupDashboardStats = async (groupId: string, date: string) => sdk.fetchWrapper.get<GroupDashboardStats>(`/groups/${groupId}/dashboardStats?date=${date}`);

    // Delivery-local phase/sequence API — a Planning's own copied tree, editable
    // without affecting the program. Mirrors the program endpoints and reuses the
    // sequence DTO shape (its programPhaseId carries the planning phase id). The phase
    // itself has its own shape: unlike the template, it carries a calendar, and that
    // calendar is mandatory (#587).
    const getPlanningPhases = async (planningId: string) =>
        sdk.fetchWrapper.get<PlanningPhasesResponse>(`/plannings/${planningId}/phases`);
    // Both phase writes refuse with a French message in the body (409): the caller passes
    // `{ errorHandler: null }` to silence the SDK's generic bubble and relay that message
    // (see utils/api-error).
    const updatePlanningPhases = async (planningId: string, data: UpdatePlanningPhasesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/phases`, data, options);
    const deletePlanningPhase = async (planningId: string, phaseId: string, moveSequencesToPhaseId?: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/plannings/${planningId}/phases/${phaseId}${moveSequencesToPhaseId ? `?moveSequencesToPhaseId=${moveSequencesToPhaseId}` : ''}`, options);
    const getPlanningModuleSequences = async (planningId: string) =>
        sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/plannings/${planningId}/moduleSequences`);
    const getPlanningPhaseModuleSequences = async (planningId: string, phaseId: string) =>
        sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/plannings/${planningId}/phases/${phaseId}/moduleSequences`);
    const updatePlanningPhaseModuleSequences = async (planningId: string, phaseId: string, data: UpdateProgramModuleSequencesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/phases/${phaseId}/moduleSequences`, data, options);

    // PlanningSlot API functions (V2 slot-based model)
    const getPlanningSlots = async (planningId: string) =>
        sdk.fetchWrapper.get<PlanningSlotsResponse>(`/plannings/${planningId}/slots`);
    const updatePlanningSlots = async (planningId: string, data: UpdatePlanningSlotsRequest) =>
        await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/slots`, data);
    const patchPlanningSlot = async (planningId: string, slotId: string, data: PatchPlanningSlotRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.patch<PlanningSlot>(`/plannings/${planningId}/slots/${slotId}`, data, options);
    // Bulk patch: reassign the editable triple of many slots in one request (one
    // fragment = many slots), instead of one PATCH per slot.
    const patchPlanningSlots = async (planningId: string, data: PatchPlanningSlotsRequest) =>
        await sdk.fetchWrapper.patch<{ updated: number }>(`/plannings/${planningId}/slots`, data);
    // #720 — covering an absence means taking its teacher off THAT span and nothing else.
    const releaseTeacherSlots = async (planningId: string, data: ReleaseTeacherSlotsRequest) =>
        await sdk.fetchWrapper.post<{ released: number }>(`/plannings/${planningId}/slots/releaseTeacher`, data);
    // #464 — where is this teacher/room already assigned in OTHER groups on these cells?
    const findSlotAssignmentConflicts = async (planningId: string, data: AssignmentConflictsRequest) =>
        await sdk.fetchWrapper.post<AssignmentConflictsResponse>(`/plannings/${planningId}/slots/assignmentConflicts`, data);
    // #464 — release a teacher/room from the listed slots of another planning (move step).
    const clearSlotAssignments = async (planningId: string, data: ClearSlotAssignmentsRequest) =>
        await sdk.fetchWrapper.post<{ updated: number }>(`/plannings/${planningId}/slots/clearAssignments`, data);
    // #464 (review) — every teacher/room already taken elsewhere on these cells (availability pills).
    const findSlotAssignmentBusy = async (planningId: string, data: AssignmentBusyRequest) =>
        await sdk.fetchWrapper.post<AssignmentBusyResponse>(`/plannings/${planningId}/slots/assignmentBusy`, data);
    // Discards the planning's slots and reseeds them (e.g. after a phase calendar change).
    const regeneratePlanningSlots = async (planningId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ reseeded: number }>(`/plannings/${planningId}/slots/regenerate`, {}, options);
    const getEffectivePlan = async (planningId: string, startDate: string, endDate?: string) => {
        const qs = endDate ? `?startDate=${startDate}&endDate=${endDate}` : `?startDate=${startDate}`;
        return sdk.fetchWrapper.get<EffectivePlanResponse>(`/plannings/${planningId}/effectivePlan${qs}`);
    };

    // Individual Planning API functions
    // `options` so a caller can pass `{ errorHandler: null }` and show the server's own
    // sentence: both routes refuse in French (#1073 — a cohort member may not own a personal
    // planning), and the SDK's default handler would replace it with « erreur HTTP 409 ».
    const createIndividualPlanning = async (frequentationId: string, data: { startDate?: string; calendarId?: string | null }, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<{ planningId: string; frequentationId: string; calendarId: string | null }>(`/frequentations/${frequentationId}/individualPlanning`, data, options);
    const deleteIndividualPlanning = async (frequentationId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/individualPlanning`, options);
    const getIndividualPlanningsByProgram = async (programId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/learningPrograms/${programId}/individualPlannings`);

    const MODULE_NAME = "Parcours.LearningModule";
    const PERMISSION_ADMINISTRATOR = MODULE_NAME + ".Administrateur";
    const PERMISSION_SUPERVISOR = MODULE_NAME + ".Superviseur";
    const PERMISSION_TEACHER = MODULE_NAME + ".Enseignant";
    // « Enseignement élargi » (#879, phase 1): the « Enseignant chef » role reads every student
    // like a Superviseur, without any of the Superviseur's routes.
    const PERMISSION_EXTENDED_TEACHER = MODULE_NAME + ".EnseignementÉlargi";
    const PERMISSION_ARCHIVE_STUDENTS = MODULE_NAME + ".ArchivageÉlèves";
    const PERMISSION_ARCHIVE_CONFIRMED_GROUPS = MODULE_NAME + ".ArchivageGroupesConfirmés";
    const PERMISSION_VIEWER = MODULE_NAME + ".Visiteur";
    // Staff notes on an exam (#1452): reading them and their count; writing them, whoever wrote
    // them. The server lets the writing permission read too.
    const PERMISSION_EXAM_NOTES_READ = MODULE_NAME + ".LectureNotesExamen";
    const PERMISSION_EXAM_NOTES_WRITE = MODULE_NAME + ".RédactionNotesExamen";

    // ================================================================
    // ATE (Alternance Travail-Études) API functions
    // ================================================================

    const ATE_MODULE = "Parcours.ATE";
    const ATE_PERMISSION_ADMINISTRATOR = ATE_MODULE + ".Administrateur";
    const ATE_PERMISSION_TEACHER = ATE_MODULE + ".Enseignant";
    const ATE_PERMISSION_SUPERVISOR = ATE_MODULE + ".Superviseur";
    const ATE_PERMISSION_VIEWER = ATE_MODULE + ".Visiteur";

    // ================================================================
    // Demonstration API functions
    // ================================================================

    // Four distinct permissions, specific to the demo engine — see DemonstrationModule.cs.
    // Administrateur carries everything (including the purge); the three others split up the
    // rest, because writing a scenario, applying it to the database and emptying the database
    // are not entrusted to the same people.
    const DEMO_MODULE = "Parcours.Demonstration";
    const DEMO_PERMISSION_ADMINISTRATOR = DEMO_MODULE + ".Administrateur";
    /** Write the configurations and generate fake people. */
    const DEMO_PERMISSION_DESIGNER = DEMO_MODULE + ".Concepteur";
    /** Apply and remove an existing configuration. */
    const DEMO_PERMISSION_PRESENTER = DEMO_MODULE + ".Démonstrateur";
    /** Read-only access to the configurations and to the state of the data. */
    const DEMO_PERMISSION_OBSERVER = DEMO_MODULE + ".Observateur";

    const getDemonstrationConfigurations = async () =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse[]>(`/demonstration/configurations`);

    const getDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`);

    /**
     * The configuration that covers every screen (#984), computed server-side from this
     * instance's referential. Nothing is persisted: the caller fills its form with it and saves
     * it like any other scenario. Typed by the caller — the shape is `demo.vue`'s `DemoConfig`,
     * which lives with the form it drives rather than in the SDK.
     */
    const getRecommendedDemonstrationConfiguration = async () =>
        sdk.fetchWrapper.get<Record<string, unknown>>(`/demonstration/configurations/recommended`);

    const createDemonstrationConfiguration = async (name: string, configurationJson: string) =>
        sdk.fetchWrapper.post<DemonstrationConfigurationResponse>(`/demonstration/configurations`, { name, configurationJson });

    const updateDemonstrationConfiguration = async (id: string, name: string, configurationJson: string) =>
        sdk.fetchWrapper.put<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`, { name, configurationJson });

    const deleteDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/demonstration/configurations/${id}`);

    const duplicateDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.post<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}/duplicate`, {});

    const generateDemoStudents = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-students`, { count });

    const generateDemoTeachers = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-teachers`, { count });

    const applyDemonstrationConfiguration = async (id: string, presenceEndDate?: string) =>
        sdk.fetchWrapper.post<ApplyConfigurationResult>(`/demonstration/configurations/${id}/apply`, { presenceEndDate });

    const removeDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.post<RemoveConfigurationResult>(`/demonstration/configurations/${id}/remove`, {});

    /**
     * Starts an apply in the background and returns the id to follow it by. Progress and the final
     * result arrive on the permanent SSE channel under `demonstration.apply` — see `runJob`.
     */
    const applyDemonstrationConfigAsync = async (id: string, presenceEndDate?: string) =>
        sdk.fetchWrapper.post<{ jobId: string }>(
            `/demonstration/configurations/${id}/apply-async`, { presenceEndDate });

    const cleanupDemoData = async () =>
        sdk.fetchWrapper.post<CleanupResult>(`/demonstration/cleanup`, {});

    const getDemoDataStats = async () =>
        sdk.fetchWrapper.get<DataStatsResult>(`/demonstration/stats`);

    // Reference Data
    const getAteRatingGroups = async () =>
        sdk.fetchWrapper.get<AteAssessmentRatingGroup[]>(`/ate/ratinggroups`);
    const createAteRatingGroup = async (data: Partial<AteAssessmentRatingGroup>, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<AteAssessmentRatingGroup>(`/ate/ratinggroups`, data, options);
    const updateAteRatingGroup = async (id: string, data: Partial<AteAssessmentRatingGroup>, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/ate/ratinggroups/${id}`, data, options);
    const archiveAteRatingGroup = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/ate/ratinggroups/${id}`, options);

    // `includeArchived` lists the archived templates too, after the active ones (#1333); the server
    // honours it for those who may restore them and answers the active ones to anyone else.
    const getAteActivityTemplates = async (moduleId: string, includeArchived = false) =>
        sdk.fetchWrapper.get<AteActivityTemplate[]>(
            `/ate/learningmodules/${moduleId}/activitytemplates${includeArchived ? '?includeArchived=true' : ''}`);
    const createAteActivityTemplate = async (data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.post<AteActivityTemplate>(`/ate/activitytemplates`, data);
    const updateAteActivityTemplate = async (id: string, data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/activitytemplates/${id}`, data);
    const archiveAteActivityTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitytemplates/${id}`);
    const restoreAteActivityTemplate = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/ate/activitytemplates/${id}/restore`, {}, options);

    const getAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates`);
    const createAteQuestionnaireTemplate = async (data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireTemplate>(`/ate/questionnairetemplates`, data);
    const updateAteQuestionnaireTemplate = async (id: string, data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${id}`, data);
    const archiveAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${id}`);
    const getArchivedAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates/archived`);
    const restoreAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnairetemplates/${id}/restore`, {});
    const createAteQuestionnaireTemplateItem = async (templateId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireItemTemplate>(`/ate/questionnairetemplates/${templateId}/items`, data);
    const updateAteQuestionnaireTemplateItem = async (templateId: string, itemId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`, data);
    const deleteAteQuestionnaireTemplateItem = async (templateId: string, itemId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`);

    // `includeArchived`: as for the activity templates above (#1333).
    const getAteHACTemplates = async (programId: string, includeArchived = false) =>
        sdk.fetchWrapper.get<AteHACTemplate[]>(
            `/ate/learningprograms/${programId}/hactemplates${includeArchived ? '?includeArchived=true' : ''}`);
    const createAteHACTemplate = async (data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.post<AteHACTemplate>(`/ate/hactemplates`, data);
    const updateAteHACTemplate = async (id: string, data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/hactemplates/${id}`, data);
    const archiveAteHACTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hactemplates/${id}`);
    const restoreAteHACTemplate = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/ate/hactemplates/${id}/restore`, {}, options);
    const createAteHACTemplateItem = async (templateId: string, data: Partial<AteHACItem>) =>
        sdk.fetchWrapper.post<AteHACItem>(`/ate/hactemplates/${templateId}/items`, data);
    const updateAteHACTemplateItem = async (templateId: string, itemId: string, data: Partial<AteHACItem>) =>
        sdk.fetchWrapper.put<void>(`/ate/hactemplates/${templateId}/items/${itemId}`, data);
    const deleteAteHACTemplateItem = async (templateId: string, itemId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hactemplates/${templateId}/items/${itemId}`);

    // Enterprises
    const getAteEnterprises = async () =>
        sdk.fetchWrapper.get<AteEnterprise[]>(`/ate/enterprises`);
    const getAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterprise>(`/ate/enterprises/${id}`);
    const createAteEnterprise = async (data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.post<AteEnterprise>(`/ate/enterprises`, data);
    const updateAteEnterprise = async (id: string, data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.put<void>(`/ate/enterprises/${id}`, data);
    const archiveAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/enterprises/${id}`);

    // Enterprise Contacts
    const getAteEnterpriseContacts = async (enterpriseId: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact[]>(`/ate/enterprises/${enterpriseId}/contacts`);
    const getAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact>(`/ate/contacts/${id}`);
    const createAteEnterpriseContact = async (enterpriseId: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.post<AteEnterpriseContact>(`/ate/enterprises/${enterpriseId}/contacts`, data);
    const updateAteEnterpriseContact = async (id: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.put<void>(`/ate/contacts/${id}`, data);
    const archiveAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/contacts/${id}`);

    // Work Placements
    const getMyAteWorkPlacements = async () =>
        sdk.fetchWrapper.get<AteStudentStageSummary[]>(`/ate/mine/workplacements`);
    const getMyAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.get<AteStudentStageDetail>(`/ate/mine/workplacements/${id}`);
    const createMyAteJournalEntry = async (id: string, data: AteStudentJournalRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<AteStudentJournalEntry>(`/ate/mine/workplacements/${id}/journal`, data, options);
    const updateMyAteJournalEntry = async (workPlacementId: string, journalEntryId: string, data: AteStudentJournalRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<AteStudentJournalEntry>(`/ate/mine/workplacements/${workPlacementId}/journal/${journalEntryId}`, data, options);
    const getAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement>(`/ate/workplacements/${id}`);
    const getAteWorkPlacementsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/frequentations/${frequentationId}/workplacements`);
    const getAteWorkPlacementsByGroup = async (groupId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/groups/${groupId}/workplacements`);
    const getAteGroupDashboard = async (groupId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementDashboardItem[]>(`/ate/groups/${groupId}/dashboard`);
    // Establishment-wide (or site-/program-scoped) landing summary (#723): aggregated server-side,
    // unlike getAteGroupDashboard above which downloads the whole group and is filtered client-side.
    const getAteDashboardSummary = async (
        options: { siteId?: string; programId?: string; includeArchived?: boolean } = {}
    ) => {
        const params = new URLSearchParams();
        if (options.siteId) params.set('siteId', options.siteId);
        if (options.programId) params.set('programId', options.programId);
        // ALWAYS sent, never conditionally. The `if (x)` guard is right above, for optional
        // GUIDs — but on a boolean it swallows `false`, and the parameter is a non-nullable
        // `bool` on the server side: the request then went without it and came back as a 400,
        // so the page did not load at all. This is also the shape of the three other SDK calls
        // that carry a flag (`params.append(..., x.toString())`).
        params.set('includeArchived', String(options.includeArchived ?? false));
        const query = params.toString();
        return sdk.fetchWrapper.get<AteWorkPlacementDashboardSummary>(`/ate/dashboard/summary${query ? `?${query}` : ''}`);
    };
    const createAteWorkPlacement = async (workPlacement: Partial<AteWorkPlacement>, moduleIds: string[], options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ id: string }>(`/ate/workplacements`, { workPlacement, moduleIds }, options);
    const updateAteWorkPlacement = async (id: string, data: Partial<AteWorkPlacement>) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}`, data);
    const archiveAteWorkPlacement = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}/archive`, {}, options);
    // « Restaurer » (#1334): an archived placement is read-only until then.
    const restoreAteWorkPlacement = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}/restore`, {}, options);
    const assignAteContactToWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.post<void>(`/ate/workplacements/${id}/contacts/${contactId}`, {});
    const removeAteContactFromWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/workplacements/${id}/contacts/${contactId}`);

    // Work Placement Notes
    const getAteWorkPlacementNotes = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementNote[]>(`/ate/workplacements/${workPlacementId}/notes`);
    const createAteWorkPlacementNote = async (workPlacementId: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementNote>(`/ate/workplacements/${workPlacementId}/notes`, data);
    const updateAteWorkPlacementNote = async (id: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.put<void>(`/ate/notes/${id}`, data);
    const deleteAteWorkPlacementNote = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/ate/notes/${id}`, options);

    // Journal (Work Placement Activities)
    const getAteWorkPlacementActivities = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity[]>(`/ate/workplacements/${workPlacementId}/activities`);
    const getAteWorkPlacementActivity = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity>(`/ate/activities/${id}`);
    const createAteWorkPlacementActivity = async (workPlacementId: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivity>(`/ate/workplacements/${workPlacementId}/activities`, data);
    const updateAteWorkPlacementActivity = async (id: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.put<void>(`/ate/activities/${id}`, data);
    const deleteAteWorkPlacementActivity = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/ate/activities/${id}`, options);
    const addAteActivityNote = async (activityId: string, data: Partial<AteWorkPlacementActivityNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivityNote>(`/ate/activities/${activityId}/notes`, data);
    const deleteAteActivityNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitynotes/${noteId}`);

    // Assessments
    const getAteActivityAssessments = async (activityId: string) =>
        sdk.fetchWrapper.get<AteActivityAssessment[]>(`/ate/activities/${activityId}/assessments`);
    const createAteActivityAssessment = async (activityId: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.post<AteActivityAssessment>(`/ate/activities/${activityId}/assessments`, data);
    const updateAteActivityAssessment = async (id: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.put<void>(`/ate/assessments/${id}`, data);
    const deleteAteActivityAssessment = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/assessments/${id}`);

    // Visits
    const getAteVisits = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit[]>(`/ate/workplacements/${workPlacementId}/visits`);
    const getAteVisit = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit>(`/ate/visits/${id}`);
    const createAteVisit = async (workPlacementId: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisit>(`/ate/workplacements/${workPlacementId}/visits`, data);
    const updateAteVisit = async (id: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.put<void>(`/ate/visits/${id}`, data);
    const deleteAteVisit = async (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/ate/visits/${id}`, options);
    const addAteVisitNote = async (visitId: string, data: Partial<AteWorkPlacementVisitNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisitNote>(`/ate/visits/${visitId}/notes`, data);
    const deleteAteVisitNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/visitnotes/${noteId}`);

    // Questionnaires
    const getAteQuestionnaires = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire[]>(`/ate/workplacements/${workPlacementId}/questionnaires`);
    const getAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire>(`/ate/questionnaires/${id}`);
    const generateAteQuestionnaire = async (workPlacementId: string, templateId: string, visitId?: string | null, contactId?: string | null) =>
        sdk.fetchWrapper.post<AteQuestionnaire>(`/ate/workplacements/${workPlacementId}/questionnaires/generate`, { templateId, visitId, contactId });
    const sendAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/send`, {});
    const respondToAteQuestionnaireItem = async (itemId: string, content: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaireitems/${itemId}/respond`, { content });
    const completeAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/complete`, {});

    // HAC
    const getAteHACs = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteHAC[]>(`/ate/workplacements/${workPlacementId}/hacs`);
    const getAteHAC = async (id: string) =>
        sdk.fetchWrapper.get<AteHAC>(`/ate/hacs/${id}`);
    const createAteHAC = async (workPlacementId: string, templateId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<AteHAC>(`/ate/workplacements/${workPlacementId}/hacs`, { templateId }, options);
    const upsertAteHACEntry = async (hacId: string, data: Partial<AteHACEntry>) =>
        sdk.fetchWrapper.post<AteHACEntry>(`/ate/hacs/${hacId}/entries`, data);
    const deleteAteHACEntry = async (entryId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hac-entries/${entryId}`);

    // Scheduling / Simulation
    /**
     * Asks the server to halt the running solve identified by `solveId`. The
     * solver returns its latest incumbent through the still-open SSE stream.
     */
    const cancelScheduling = async (solveId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/scheduling-v2/cancel/${solveId}`, {}, options);

    /**
     * Starts a group solve in the background and returns the id to follow it by. Progress and the
     * final result arrive on the permanent SSE channel under `scheduling.groups` — see `runJob`.
     * The backend rebuilds the V2 request from the groupIds, solves, and translates the result back
     * to the legacy SchedulingSolverResponse shape so simulation.vue consumes it unchanged.
     */
    const solveGroupSchedulingAsync = async (data: SolveGroupsRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ jobId: string }>(`/Plannings/Solve-Groups-Async`, data, options);

    /** Whether each change a refusal's remedies asked for is in the data now: the solver page's ticks. */
    const checkSolverRemedies = async (data: RemedyCheckRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<RemedyCheckResponse>(`/Plannings/Remedy-Checks`, data, options);

    /**
     * Before any run, what would make the engine refuse the request « Générer » would send — a competence
     * no teacher can give, a requirement no allowed room meets — each point with its fixes.
     */
    const precheckSolverGroups = async (data: SolvePrecheckRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<SolvePrecheckResponse>(`/Plannings/Solve-Precheck`, data, options);

    /**
     * Atomically replaces the slots of several plannings in one transaction.
     * Stateless apply path for a chosen simulation solution: the client sends
     * the slots it already holds from the rendered solution.
     */
    const applyPlanningSlotsBatch = async (data: ApplyPlanningSlotsRequest, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<ApplyPlanningSlotsResponse>(`/Plannings/Slots/Batch`, data, options);

    // Scheduling V2 (slot-based solver)
    const solveSchedulingV2 = async (data: SchedulingV2Request) =>
        sdk.fetchWrapper.post<SchedulingV2Response>(`/scheduling-v2/solve`, data, { errorHandler: null });
    const cancelSchedulingV2 = async (solveId: string) =>
        sdk.fetchWrapper.post<void>(`/scheduling-v2/cancel/${solveId}`, {});
    /**
     * Starts a solve in the background and returns the id to follow it by. Progress and the final
     * result arrive on the permanent SSE channel under `scheduling.progress` — see `runJob`.
     */
    const solveSchedulingV2Async = async (data: SchedulingV2Request) =>
        sdk.fetchWrapper.post<{ jobId: string }>(`/scheduling-v2/solve-async`, data, { errorHandler: null });
    const getSchedulingV2SampleRequest = async (preset: DatasetPresetV2, seed = 42) =>
        sdk.fetchWrapper.get<SchedulingV2Request>(`/scheduling-v2/sample-request?preset=${preset}&seed=${seed}`);

    // ── Student submissions (Arkiv, LOT3 #629/#634) ──────────────────────────
    // JSON calls of the frozen contract (StudentSubmissionsRouteDto.cs): the bytes NEVER
    // go through here — uploading/downloading contents happens through the fondation proxy
    // /api/core/proxy/arkiv/documents/{arkivDocumentId}/versions[...] (see utils/arkiv.ts
    // and the use-submission-upload composable).

    // Student routes (self-service "Mine")
    /** Starts an absence justification: range, comment, file descriptors. */
    const createMyAbsenceJustification = async (frequentationId: string, data: CreateAbsenceJustificationRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<CreateSubmissionResponse>(`/frequentations/mine/${frequentationId}/absenceJustifications`, data, options);
    /** Initiation d'une remise d'examen. */
    const createMyExamSubmission = async (frequentationId: string, examId: string, data: CreateExamSubmissionRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<CreateSubmissionResponse>(`/frequentations/mine/${frequentationId}/exams/${examId}/submissions`, data, options);
    /** Confirms the submission once the bytes are uploaded to Arkiv (Status Pending → Ready). */
    const confirmMySubmission = async (submissionId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<StudentSubmissionResponse>(`/submissions/mine/${submissionId}/confirm`, {}, options);
    /** My submissions for a frequentation, optional filter by kind. */
    const getMyFrequentationSubmissions = async (frequentationId: string, kind?: SubmissionKind) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/frequentations/mine/${frequentationId}/submissions${kind ? `?kind=${kind}` : ''}`);
    /** Adds files to an existing submission. */
    const addMySubmissionFiles = async (submissionId: string, data: AddSubmissionFilesRequest, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.post<AddSubmissionFilesResponse>(`/submissions/mine/${submissionId}/files`, data, options);
    /** Removes a file from a submission. */
    const removeMySubmissionFile = async (submissionId: string, fileId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/submissions/mine/${submissionId}/files/${fileId}`, options);
    /** Deletes the submission (and its Arkiv documents). */
    const deleteMySubmission = async (submissionId: string, options?: SdkRequestOptions) =>
        await sdk.fetchWrapper.delete<void>(`/submissions/mine/${submissionId}`, options);

    // Staff routes (local reads — they answer even if Arkiv is down)
    /** Every submission of a frequentation. */
    const getFrequentationSubmissions = async (frequentationId: string) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/frequentations/${frequentationId}/submissions`);
    /** Batch for the assiduity grid: submissions covering the given presences. */
    const getSubmissionsByPresences = async (presenceIds: string[]) =>
        sdk.fetchWrapper.get<PresenceSubmissionsResponse>(`/presences/submissions?presenceIds=${presenceIds.map(encodeURIComponent).join(',')}`);
    /** Every submission of a student (all frequentations). */
    const getSubmissionsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<StudentSubmissionsResponse>(`/students/${studentId}/submissions`);

    // Initialization status
    const getInitializationStatus = async () => sdk.fetchWrapper.get<InitializationStatusResponse>(`/initializationStatus`);

    return {
        learningPrograms,
        availableLearningPrograms,
        getLearningProgram,
        addLearningProgram,
        updateLearningProgram,
        setLearningProgramAvailability,
        cloneLearningProgram,
        learningModules,
        learningModulesByProgram,
        getAvailableLearningModules,
        getLearningModule,
        addLearningModule,
        updateLearningModule,
        setLearningModuleResponsibleTeacher,
        setLearningModuleTags,
        setLearningModuleNormativeHours,
        archiveLearningModule,
        restoreLearningModule,
        archivedLearningModulesByProgram,
        exportLearningData,
        importLearningData,
        teachers,
        getTeacher,
        teachersPaged,
        meqProgrammesPaged,
        getMeqProgramme,
        getMeqDependencies,
        generateMeqDependenciesReport,
        getCurrentTeacher,
        getTeacherPlanning,
        getTeacherArchiveImpact,
        getTeacherTransferInventory,
        archiveTeacherWithTransfer,
        getProgramHoursTransferInventory,
        transferProgramHours,
        getPlanningHoursTransferInventory,
        transferPlanningHours,
        restoreTeacher,
        getTeachersCurrentActivity,
        getGroupPlanning,
        isTeacherGroupMappedToEntraId,
        teachersByProgram,
        getAvailableTeachers,
        addTeacher,
        addTeachersFromEntraIdGroup,
        archiveTeacher,
        setTeacherColor,
        setTeacherSpecialistType,
        getTeacherLearningPrograms,
        assignTeacherToProgram,
        removeTeacherFromProgram,
        getTeacherLearningModules,
        setTeacherProgramModules,
        getTeacherUnavailableRanges,
        getTeacherWorkAvailability,
        getTeacherAvailabilities,
        createTeacherAvailability,
        updateTeacherAvailability,
        setTeacherAvailabilityEndDate,
        deleteTeacherAvailability,
        frequentations,
        myFrequentations,
        myFrequentationPresences,
        myFrequentationLearningModules,
        myFrequentationModulePlans,
        generateMyStudentScheduleReport,
        generateMyStudentCalendarReport,
        generateMyProgressionReport,
        generateMyAbsenceReport,
        myBulkUpsertPresences,
        getFrequentationById,
        getScheduleChainInfo,
        applyScheduleChainOperation,
        getPlanningAvailability,
        createPlanningAvailabilityFrame,
        updatePlanningAvailabilityFrame,
        deletePlanningAvailabilityFrame,
        createPlanningUnavailability,
        updatePlanningUnavailability,
        deletePlanningUnavailability,
        relayPlanningAvailability,
        frequentationsByTeacher,
        frequentationsByProgram,
        getStudentById,
        getStudentByUserId,
        frequentationsByStudent,
        addFrequentation,
        addFrequentationsFromEntraIdGroup,
        updateFrequentation,
        updateFrequentationSecondaryStaff,
        getSecondaryStaffRoles,
        frequentationsPaged,
        getFrequentationSummary,
        updateStudentCoordinates,
        getStudentAddresses,
        createStudentAddress,
        updateStudentAddress,
        deleteStudentAddress,
        searchStudentsWithFrequentation,
        searchEnrollableUsers,
        searchStudents,
        studentsPaged,
        addStudent,
        archiveStudent,
        restoreStudent,
        addPresence,
        updatePresence,
        deletePresence,
        bulkUpsertPresences,
        getGroupCourses,
        getGroupCoursesSummary,
        getGroupCourseDefaults,
        getGroupCourse,
        previewGroupCourse,
        createGroupCourse,
        updateGroupCourse,
        archiveGroupCourse,
        addGroupCourseSession,
        moveGroupCourseSession,
        cancelGroupCourseSession,
        takeGroupCourse,
        dropGroupCourse,
        myTakeGroupCourse,
        myDropGroupCourse,
        getPresencesByDateRange,
        getTeacherPresencesByDateRange,
        getPresenceSummary,
        getTeacherLoadSummary,
        getAbsenceDay,
        getAbsenceRange,
        getStudentNotesByPresence,
        getStudentNotes,
        createStudentNoteByPresence,
        createStudentNote,
        updateStudentNote,
        deleteStudentNote,
        getStudentSharedNotes,
        createStudentSharedNote,
        updateStudentSharedNote,
        deleteStudentSharedNote,
        mySharedNotes,
        getExamNoteCounts,
        getExamNotes,
        createExamNote,
        updateExamNote,
        deleteExamNote,
        exams,
        examsFiltered,
        myExams,
        examsCount,
        getExam,
        getExamsByFrequentation,
        createExam,
        createGroupExams,
        examServiceTypeSuggestions,
        updateExam,
        deleteExam,
        getExamSupervisionDaySheet,
        recordExamSupervisionPresences,
        getMyFollowedStudents,
        getSpecialistDirectory,
        getSpecialistStudent,
        getSpecialistStudentSchedule,
        getSpecialistBookingRooms,
        bookSpecialistPeriod,
        cancelSpecialistPeriod,
        getSpecialists,
        getMySpecialistDaySheet,
        recordMySpecialistPresences,
        getMySpecialistAgenda,
        getMySpecialistGroups,
        getMySpecialistGroupAttendance,
        getMySpecialistGroupProgression,
        getSpecialistRoster,
        getSpecialistCandidates,
        registerSpecialist,
        getConsolidatedView,
        generateExamAssignmentReport,
        generateAbsenceReport,
        generateWeeklyPresenceReport,
        generateProgressionReport,
        generateResultTransmissionModuleReport,
        generateResultTransmissionAllModulesReport,
        generateDepartureSheetReport,
        generateReturnSheetReport,
        generateRetakeRefusalReport,
        generateFrequentationInfoReport,
        generateStudentScheduleReport,
        generateAtRiskReport,
        generateModulePerformanceReport,
        generateTeacherWorkloadReport,
        generateTeacherAnnualLoadReport,
        generateTeacherAssignmentCalendarReport,
        generateCalendarYearReport,
        generateScheduleCalendarReport,
        generateAttendanceTrendReport,
        generateCumulativeHoursReport,
        generateWorkStudyTimeReport,
        generateNormativeHoursReport,
        getStudentJourney,
        getFrequentationModuleResults,
        getFrequentationModuleStatuses,
        setModuleStatus,
        deleteModuleStatus,
        getMyFrequentationModuleLocks,
        getMyFrequentationModuleHours,
        getMyFrequentationModuleAvailability,
        getMySequenceBlocks,
        getSequenceBlocksOfFrequentations,
        getFrequentationModuleLocks,
        getFrequentationModuleAvailabilityOutlook,
        setFrequentationModuleLock,
        setAllFrequentationModuleLocks,
        getModulePlan,
        getModulePlansBatch,
        getScheduleAxis,
        getTeacherModulePlans,
        getTeacherDaySheet,
        getProgramDaySheet,
        notifyUnchosenPeriod,
        getTeacherDailyReport,
        getStudentsWithRecentAbsences,
        getStudentsExceedingModuleTime,
        getStudentsWithDataIssues,
        getCssPresenceStats,
        getPlannings,
        getPlanning,
        createPlanning,
        updatePlanning,
        setPlanningEndDate,
        getPlanningEarliestEndDate,
        deletePlanning,
        groups,
        getActiveGroups,
        getGroup,
        createGroup,
        duplicateGroup,
        updateGroup,
        deleteGroup,
        // Site (Centre) functions
        getSites,
        getSite,
        createSite,
        updateSite,
        getSiteLogo,
        uploadSiteLogo,
        deleteSiteLogo,
        setProgramSites,
        getGricsImportAvailability,
        getGricsImportOverview,
        getGricsImportRun,
        startGricsImportRun,
        cancelGricsImportRun,
        // Room functions
        getRooms,
        getRoomArchiveImpact,
        getRoomOccupancy,
        getRoomsBySite,
        getRoomsByEnterprise,
        getRoomOwnerEnterprises,
        getRoom,
        createRoom,
        updateRoom,
        // Resource functions
        getResources,
        getResourcesBySite,
        getResource,
        createResource,
        updateResource,
        setResourcePrograms,
        // FrequentationTag functions
        getFrequentationTags,
        createFrequentationTag,
        updateFrequentationTag,
        deleteFrequentationTag,
        addFrequentationTagValue,
        updateFrequentationTagValue,
        deleteFrequentationTagValue,
        getTagsByStudent,
        setStudentTags,
        // FrequentationNoteTypeDefinition functions
        getFrequentationNoteTypeDefinitions,
        createFrequentationNoteTypeDefinition,
        updateFrequentationNoteTypeDefinition,
        deleteFrequentationNoteTypeDefinition,
        reorderFrequentationNoteTypeDefinitions,
        // PresenceTypeDefinition functions
        getPresenceTypeDefinitions,
        createPresenceTypeDefinition,
        updatePresenceTypeDefinition,
        deletePresenceTypeDefinition,
        reorderPresenceTypeDefinitions,
        // PresenceStatusDefinition functions
        getPresenceStatusDefinitions,
        createPresenceStatusDefinition,
        updatePresenceStatusDefinition,
        deletePresenceStatusDefinition,
        reorderPresenceStatusDefinitions,
        getGroupMembers,
        getAvailableGroupMembers,
        addGroupMember,
        removeGroupMember,
        importJadeRoster,
        importJadeResults,
        importJadeFrequentations,
        importAdmissionFpRoster,
        importIndividualJadeRoster,
        importIndividualAdmissionFpRoster,
        getProgramModuleSequences,
        updateProgramModuleSequences,
        getProgramPhaseModuleSequences,
        updateProgramPhaseModuleSequences,
        getProgramPhases,
        updateProgramPhases,
        deleteProgramPhase,
        getProgramGroupMembers,
        getGroupStats,
        getGroupDashboardStats,
        getPlanningPhases,
        updatePlanningPhases,
        deletePlanningPhase,
        getPlanningModuleSequences,
        getPlanningPhaseModuleSequences,
        updatePlanningPhaseModuleSequences,
        getPlanningSlots,
        updatePlanningSlots,
        patchPlanningSlot,
        patchPlanningSlots,
        releaseTeacherSlots,
        findSlotAssignmentConflicts,
        findSlotAssignmentBusy,
        clearSlotAssignments,
        regeneratePlanningSlots,
        getEffectivePlan,
        // Individual Planning
        createIndividualPlanning,
        deleteIndividualPlanning,
        getIndividualPlanningsByProgram,
        // ATE
        ATE_MODULE,
        ATE_PERMISSION_ADMINISTRATOR,
        ATE_PERMISSION_TEACHER,
        ATE_PERMISSION_SUPERVISOR,
        ATE_PERMISSION_VIEWER,
        getAteRatingGroups,
        createAteRatingGroup,
        updateAteRatingGroup,
        archiveAteRatingGroup,
        getAteActivityTemplates,
        createAteActivityTemplate,
        updateAteActivityTemplate,
        archiveAteActivityTemplate,
        restoreAteActivityTemplate,
        getAteQuestionnaireTemplates,
        createAteQuestionnaireTemplate,
        updateAteQuestionnaireTemplate,
        archiveAteQuestionnaireTemplate,
        getArchivedAteQuestionnaireTemplates,
        restoreAteQuestionnaireTemplate,
        createAteQuestionnaireTemplateItem,
        updateAteQuestionnaireTemplateItem,
        deleteAteQuestionnaireTemplateItem,
        getAteHACTemplates,
        createAteHACTemplate,
        updateAteHACTemplate,
        archiveAteHACTemplate,
        restoreAteHACTemplate,
        createAteHACTemplateItem,
        updateAteHACTemplateItem,
        deleteAteHACTemplateItem,
        getAteEnterprises,
        getAteEnterprise,
        createAteEnterprise,
        updateAteEnterprise,
        archiveAteEnterprise,
        getAteEnterpriseContacts,
        getAteEnterpriseContact,
        createAteEnterpriseContact,
        updateAteEnterpriseContact,
        archiveAteEnterpriseContact,
        getAteWorkPlacement,
        getMyAteWorkPlacements,
        getMyAteWorkPlacement,
        createMyAteJournalEntry,
        updateMyAteJournalEntry,
        getAteWorkPlacementsByFrequentation,
        getAteWorkPlacementsByGroup,
        getAteGroupDashboard,
        getAteDashboardSummary,
        createAteWorkPlacement,
        updateAteWorkPlacement,
        archiveAteWorkPlacement,
        restoreAteWorkPlacement,
        assignAteContactToWorkPlacement,
        removeAteContactFromWorkPlacement,
        getAteWorkPlacementNotes,
        createAteWorkPlacementNote,
        updateAteWorkPlacementNote,
        deleteAteWorkPlacementNote,
        getAteWorkPlacementActivities,
        getAteWorkPlacementActivity,
        createAteWorkPlacementActivity,
        updateAteWorkPlacementActivity,
        deleteAteWorkPlacementActivity,
        addAteActivityNote,
        deleteAteActivityNote,
        getAteActivityAssessments,
        createAteActivityAssessment,
        updateAteActivityAssessment,
        deleteAteActivityAssessment,
        getAteVisits,
        getAteVisit,
        createAteVisit,
        updateAteVisit,
        deleteAteVisit,
        addAteVisitNote,
        deleteAteVisitNote,
        getAteQuestionnaires,
        getAteQuestionnaire,
        generateAteQuestionnaire,
        sendAteQuestionnaire,
        respondToAteQuestionnaireItem,
        completeAteQuestionnaire,
        getAteHACs,
        getAteHAC,
        createAteHAC,
        upsertAteHACEntry,
        deleteAteHACEntry,
        PERMISSION_ADMINISTRATOR,
        PERMISSION_SUPERVISOR,
        PERMISSION_TEACHER,
        PERMISSION_EXTENDED_TEACHER,
        PERMISSION_ARCHIVE_STUDENTS,
        PERMISSION_ARCHIVE_CONFIRMED_GROUPS,
        PERMISSION_VIEWER,
        PERMISSION_EXAM_NOTES_READ,
        PERMISSION_EXAM_NOTES_WRITE,
        calendarSdk: sdk.Calendar,
        schedulePatternSdk: sdk.SchedulePattern,
        scheduleAssignmentSdk: sdk.ScheduleAssignment,
        // Demonstration
        DEMO_MODULE,
        DEMO_PERMISSION_ADMINISTRATOR,
        DEMO_PERMISSION_DESIGNER,
        DEMO_PERMISSION_PRESENTER,
        DEMO_PERMISSION_OBSERVER,
        getDemonstrationConfigurations,
        getDemonstrationConfiguration,
        getRecommendedDemonstrationConfiguration,
        createDemonstrationConfiguration,
        updateDemonstrationConfiguration,
        deleteDemonstrationConfiguration,
        duplicateDemonstrationConfiguration,
        generateDemoStudents,
        generateDemoTeachers,
        applyDemonstrationConfiguration,
        applyDemonstrationConfigAsync,
        removeDemonstrationConfiguration,
        cleanupDemoData,
        getDemoDataStats,
        // Scheduling / Simulation
        solveGroupSchedulingAsync,
        checkSolverRemedies,
        precheckSolverGroups,
        applyPlanningSlotsBatch,
        cancelScheduling,
        // Scheduling V2
        solveSchedulingV2,
        cancelSchedulingV2,
        solveSchedulingV2Async,
        getSchedulingV2SampleRequest,
        // Student submissions (Arkiv)
        createMyAbsenceJustification,
        createMyExamSubmission,
        confirmMySubmission,
        getMyFrequentationSubmissions,
        addMySubmissionFiles,
        removeMySubmissionFile,
        deleteMySubmission,
        getFrequentationSubmissions,
        getSubmissionsByPresences,
        getSubmissionsByStudent,
        // Initialization
        getInitializationStatus,
        getVacancies,
        createVacancy,
        archiveVacancy,
        hireVacancy,
        renameVacancy,
    }
}
